<template>
  <div class="incall-tickets-block">
    <div v-if="showTicketActionModal" class="modal-overlay" @click="closeTicketActionModal">
      <div class="modal-content modal-small" @click.stop>
        <div class="modal-header modal-header-view">
          <h3>Apri Ticket</h3>
          <button class="close-button" @click="closeTicketActionModal">×</button>
        </div>
        <div class="modal-body">
          <p class="ticket-action-preview">{{ ticketForAction?.title || ticketForAction?.titolo }}</p>
          <p class="ticket-action-hint">Come vuoi procedere?</p>
          <div class="ticket-action-buttons">
            <button class="btn-secondary ticket-action-btn" type="button" @click="selectTicketAction('view')">
              <i class="pi pi-eye"></i>
              Visualizza
            </button>
            <button class="btn-primary ticket-action-btn" type="button" @click="selectTicketAction('reply')">
              <i class="pi pi-reply"></i>
              Rispondi
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showTicketModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content modal-large" @click.stop>
        <div class="modal-header modal-header-view">
          <h3>{{ ticketModalMode === 'reply' ? 'Rispondi al Ticket' : 'Dettagli Ticket' }}</h3>
          <button class="close-button" @click="closeModal">×</button>
        </div>
        <div class="modal-body">
          <div class="ticket-details ticket-details-summary">
            <div class="detail-row">
              <strong>Tipo:</strong> {{ selectedTicket.tipo || 'Ticket' }}
            </div>
            <div class="detail-row">
              <strong>Titolo:</strong> {{ selectedTicket.title || selectedTicket.titolo }}
            </div>
            <div class="detail-row">
              <strong>Data:</strong> {{ formatDate(selectedTicket.created_at || selectedTicket.data) }}
            </div>
            <div class="detail-row">
              <strong>Stato:</strong> {{ selectedTicket.status || selectedTicket.stato }}
            </div>
            <div class="detail-row">
              <strong>Destinatario:</strong> {{ selectedTicket.recipient_name || selectedTicket.destinatario }}
            </div>
            <div class="detail-row" v-if="getSelectedTicketAssignmentLabel()">
              <strong>Assegnato a:</strong> {{ getSelectedTicketAssignmentLabel() }}
            </div>
            <div class="detail-row" v-if="selectedTicket.creator_name || selectedTicket.creator">
              <strong>Creato da:</strong> {{ selectedTicket.creator_name || selectedTicket.creator }}
            </div>
            <div class="detail-row" v-if="selectedTicket.area">
              <strong>Area:</strong> {{ selectedTicket.area }}
            </div>
            <div class="detail-row" v-if="hasCallTransferInfo(selectedTicket)">
              <strong>Trasferimento chiamata:</strong>
              <span :class="getCallTransferBadgeClass(selectedTicket)">
                {{ getTicketCallTransferLabel(selectedTicket) }}
              </span>
            </div>
          </div>

          <div v-if="selectedTicketHistoryStatus.length" class="ticket-history-section">
            <h4 class="ticket-conversation-title">Storico stato</h4>
            <div class="fatture-table-wrapper">
              <table class="fatture-table ticket-history-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Stato</th>
                    <th>Utente</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(entry, index) in selectedTicketHistoryStatus" :key="`status-${index}`">
                    <td>{{ formatTicketHistoryDate(entry.datetime) }}</td>
                    <td>{{ entry.status }}</td>
                    <td>{{ entry.name || entry.TYPE || 'N/A' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-if="selectedTicketHistoryAssignment.length" class="ticket-history-section">
            <h4 class="ticket-conversation-title">Storico assegnazioni</h4>
            <div class="fatture-table-wrapper">
              <table class="fatture-table ticket-history-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Da</th>
                    <th>A</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(entry, index) in selectedTicketHistoryAssignment" :key="`assignment-${index}`">
                    <td>{{ formatTicketHistoryDate(entry.datetime) }}</td>
                    <td>{{ entry.user_from || 'N/A' }}</td>
                    <td>{{ entry.TYPE === 'AREA' ? entry.area_to : (entry.user_to || 'N/A') }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="ticket-conversation-section">
            <h4 class="ticket-conversation-title">Conversazione</h4>
            <div v-if="loadingTicketDetails" class="ticket-conversation-loading">
              <i class="pi pi-spin pi-spinner"></i>
              Caricamento conversazione...
            </div>
            <div v-else ref="ticketMessagesContainer" class="ticket-messages-list">
              <div v-if="selectedTicketMessages.length === 0" class="no-data-message">
                Nessun messaggio nella conversazione
              </div>
              <div
                v-for="message in selectedTicketMessages"
                :key="message.id_ticket_message"
                :class="['ticket-message', isOwnTicketMessage(message) ? 'ticket-message-own' : 'ticket-message-other']"
              >
                <div class="ticket-message-meta">
                  {{ formatTicketMessageTime(message) }} · {{ getTicketMessageSender(message) }}
                </div>
                <div
                  v-if="hasCallTransferInfo(message)"
                  class="ticket-message-transfer"
                  :class="getCallTransferBadgeClass(message)"
                >
                  <i class="pi pi-phone"></i>
                  {{ getTicketCallTransferLabel(message) }}
                </div>
                <div class="ticket-message-content" v-html="formatTicketMessageContent(message.content)"></div>
                <div v-if="messageAttachments(message).length" class="ticket-message-attachments">
                  <div
                    v-for="(attachment, attachmentIndex) in messageAttachments(message)"
                    :key="`${attachmentKey(attachment)}-${attachmentIndex}`"
                    class="ticket-message-attachment"
                  >
                    <span class="ticket-message-attachment-name">
                      <i class="pi pi-file"></i>
                      {{ attachment.name || 'Allegato' }}
                    </span>
                    <span class="ticket-message-attachment-actions">
                      <button type="button" title="Apri allegato" @click="openTicketAttachment(attachment)">
                        <i class="pi pi-eye"></i>
                      </button>
                      <button type="button" title="Scarica allegato" @click="downloadTicketAttachment(attachment)">
                        <i class="pi pi-download"></i>
                      </button>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="showTicketReplyForm" class="ticket-reply-form">
            <label class="form-label" for="ticketReplyMessage">
              <i class="pi pi-comment"></i> La tua risposta
            </label>
            <textarea
              id="ticketReplyMessage"
              v-model="ticketReplyMessage"
              class="form-textarea"
              rows="4"
              placeholder="Scrivi una risposta..."
            ></textarea>

            <div class="ticket-reply-transfer-fields">
              <div class="form-group">
                <label class="form-label" for="ticketReplyStatus">
                  <i class="pi pi-flag"></i> Stato ticket
                </label>
                <select
                  id="ticketReplyStatus"
                  v-model="ticketReplyStatusId"
                  class="form-select"
                >
                  <option
                    v-for="status in ticketStatuses"
                    :key="`reply-status-${status.id_ticket_status}`"
                    :value="status.id_ticket_status"
                  >
                    {{ status.status }}
                  </option>
                </select>
              </div>

              <div class="form-group">
                <label class="form-label" for="ticketReplyAssignType">
                  <i class="pi pi-user-plus"></i> Assegna ticket
                </label>
                <select
                  id="ticketReplyAssignType"
                  v-model="ticketReplyAssignType"
                  class="form-select"
                  @change="onTicketReplyAssignTypeChange"
                >
                  <option value="">Non modificare assegnazione</option>
                  <option v-for="type in typesRecipients" :key="`reply-assign-type-${type.name}`" :value="type.name">
                    {{ type.code }}
                  </option>
                </select>
              </div>

              <div class="form-group" v-if="ticketReplyAssignType === 'area'">
                <label class="form-label" for="ticketReplyAssignArea">
                  <i class="pi pi-sitemap"></i> Area assegnazione
                </label>
                <select
                  id="ticketReplyAssignArea"
                  v-model="ticketReplyAssignRecipient"
                  class="form-select"
                >
                  <option :value="null" disabled>-- Seleziona area --</option>
                  <option v-for="area in ticketAreas" :key="`reply-assign-area-${area.id_ticket_area}`" :value="area">
                    {{ area.area }} ({{ area.code }})
                  </option>
                </select>
              </div>

              <div class="form-group" v-else-if="ticketReplyAssignType">
                <label class="form-label" for="ticketReplyAssignSearch">
                  <i class="pi pi-search"></i> Destinatario assegnazione
                </label>
                <div class="recipient-search-container">
                  <input
                    id="ticketReplyAssignSearch"
                    v-model="ticketReplyAssignSearchQuery"
                    class="form-input"
                    type="text"
                    placeholder="Cerca destinatario..."
                    autocomplete="off"
                    @input="handleTicketReplyAssignSearch"
                    @focus="openTicketReplyAssignSuggestions"
                    @blur="closeTicketReplyAssignSuggestions"
                  />
                  <span v-if="loadingTicketReplyAssignSuggestions" class="loading-suggestions">
                    <i class="pi pi-spin pi-spinner"></i>
                  </span>
                  <div
                    v-if="showTicketReplyAssignSuggestions"
                    class="suggestions-dropdown"
                  >
                    <div
                      v-for="recipient in ticketReplyAssignRecipients"
                      :key="`reply-assign-${recipient.type}-${recipient.id}`"
                      class="suggestion-item"
                      @mousedown.prevent="selectTicketReplyAssignRecipient(recipient)"
                    >
                      <div class="suggestion-primary">{{ recipient.label }}</div>
                      <div class="suggestion-secondary">{{ recipientTypeLabel(recipient.type) }}</div>
                    </div>
                    <div
                      v-if="!loadingTicketReplyAssignSuggestions && ticketReplyAssignRecipients.length === 0"
                      class="suggestion-item suggestion-empty"
                    >
                      {{ ticketReplyAssignEmptyMessage }}
                    </div>
                  </div>
                </div>
                <div v-if="ticketReplyAssignRecipient" class="selected-recipient-badge">
                  <span>
                    {{ ticketReplyAssignRecipient.label }}
                    ({{ recipientTypeLabel(ticketReplyAssignRecipient.type) }})
                  </span>
                  <button
                    type="button"
                    class="clear-recipient-btn"
                    title="Rimuovi destinatario"
                    @click="clearTicketReplyAssignRecipient"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div class="form-group">
                <label class="form-label" for="ticketReplyCallTransferSuccess">
                  <i class="pi pi-phone"></i> Trasferimento chiamata
                </label>
                <select
                  id="ticketReplyCallTransferSuccess"
                  v-model="ticketReplyCallTransferSuccess"
                  class="form-select"
                  @change="onTicketReplyCallTransferSuccessChange"
                >
                  <option :value="null">Non specificato</option>
                  <option :value="true">Sì, trasferimento riuscito</option>
                  <option :value="false">No, trasferimento non riuscito</option>
                </select>
              </div>

              <div class="form-group" v-if="requiresCallTransferArea(ticketReplyCallTransferSuccess)">
                <label class="form-label" for="ticketReplyCallTransferArea">
                  <i class="pi pi-directions-alt"></i> Area verso cui hai provato a trasferire
                </label>
                <select
                  id="ticketReplyCallTransferArea"
                  v-model="ticketReplyCallTransferAreaId"
                  class="form-select"
                  required
                >
                  <option :value="null" disabled>-- Seleziona area --</option>
                  <option
                    v-for="area in ticketAreas"
                    :key="`reply-area-${area.id_ticket_area}`"
                    :value="area.id_ticket_area"
                  >
                    {{ area.area }} ({{ area.code }})
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="closeModal">Chiudi</button>
          <button
            v-if="showTicketReplyForm"
            class="btn-primary"
            type="button"
            @click="sendTicketReply"
            :disabled="sendingTicketReply || !canSubmitTicketReplyForm"
          >
            <i class="pi pi-spin pi-spinner" v-if="sendingTicketReply"></i>
            <i class="pi pi-send" v-else></i>
            {{ sendingTicketReply ? 'Salvataggio...' : ticketReplySubmitLabel }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showAddTicketModal" class="modal-overlay modal-overlay-ticket" @click="closeModal">
      <div
        class="modal-content modal-ticket modal-draggable"
        :style="ticketModalPositionStyle"
        @click.stop
      >
        <div
          class="modal-header modal-header-create modal-drag-handle"
          @mousedown="startTicketModalDrag"
        >
          <h3>Nuovo Ticket</h3>
          <button class="close-button" @click="closeModal">&times;</button>
        </div>

        <div class="modal-body">
          <form class="ticket-form" @submit.prevent="createTicket">
            <div class="form-group">
              <label class="form-label" for="tipoDestinatario">
                <i class="pi pi-users"></i> Tipo Destinatario
              </label>
              <select id="tipoDestinatario" v-model="newTicket.tipoDestinatario" class="form-select"
                @change="onChangeTypeRecipient" required>
                <option value="">-- Scegli il tipo --</option>
                <option v-for="type in typesRecipients" :key="type.name" :value="type.name">
                  {{ type.code }}
                </option>
              </select>
            </div>

            <div class="form-group" v-if="newTicket.tipoDestinatario && newTicket.tipoDestinatario !== 'area'">
              <label class="form-label">
                <i class="pi pi-user"></i> Destinatario
              </label>

              <select
                v-if="!showRecipientSearch"
                v-model="newTicket.recipient"
                class="form-select"
                required
              >
                <option value="">-- Seleziona destinatario --</option>
                <option v-for="recipient in recipients" :key="`${recipient.type}-${recipient.id}`" :value="recipient">
                  {{ recipient.label }} ({{ recipientTypeLabel(recipient.type) }})
                </option>
              </select>

              <div v-else class="recipient-search-container">
                <input
                  v-model="recipientSearchQuery"
                  type="text"
                  class="form-input"
                  placeholder="Cerca destinatario..."
                  autocomplete="off"
                  @input="handleRecipientSearch"
                  @focus="openRecipientSuggestions"
                  @blur="closeRecipientSuggestions"
                />
                <div v-if="loadingRecipientSuggestions" class="loading-suggestions">
                  <i class="pi pi-spin pi-spinner"></i>
                </div>

                <div v-if="showRecipientSuggestions" class="suggestions-dropdown">
                  <div
                    v-for="recipient in recipients"
                    :key="`${recipient.type}-${recipient.id}`"
                    class="suggestion-item"
                    @mousedown.prevent="selectRecipientSuggestion(recipient)"
                  >
                    <div class="suggestion-primary">{{ recipient.label }}</div>
                    <div class="suggestion-secondary">{{ recipientTypeLabel(recipient.type) }}</div>
                  </div>
                  <div
                    v-if="!loadingRecipientSuggestions && recipients.length === 0"
                    class="suggestion-empty"
                  >
                    {{ recipientSearchEmptyMessage }}
                  </div>
                </div>

                <div v-if="newTicket.recipient" class="selected-recipient-badge">
                  <span>
                    {{ newTicket.recipient.label }}
                    ({{ recipientTypeLabel(newTicket.recipient.type) }})
                  </span>
                  <button
                    type="button"
                    class="clear-recipient-btn"
                    title="Rimuovi selezione"
                    @mousedown.prevent="clearSelectedRecipient"
                  >
                    <i class="pi pi-times"></i>
                  </button>
                </div>
              </div>
            </div>

            <div class="form-group" v-if="newTicket.tipoDestinatario === 'area'">
              <label class="form-label" for="areaDestinatario">
                <i class="pi pi-sitemap"></i> Area Destinatario
              </label>
              <select id="areaDestinatario" v-model="newTicket.recipient" class="form-select" required>
                <option value="">-- Seleziona area --</option>
                <option v-for="area in ticketAreas" :key="area.id_ticket_area" :value="area">
                  {{ area.area }} ({{ area.code }})
                </option>
              </select>
            </div>

            <div class="form-group">
              <label class="form-label" for="titoloTicket">
                <i class="pi pi-tag"></i> Titolo Ticket
              </label>
              <div class="input-with-prefix" v-if="motivazioneTicketTesto">
                <span class="input-prefix">{{ motivazioneTicketTesto }} </span>
                <input id="titoloTicket" type="text" v-model="newTicket.titolo" class="form-input"
                  placeholder="Inserisci il titolo (opzionale)..." :required="!newTicket.motivazioneId" maxlength="100" />
              </div>
              <input v-else id="titoloTicket" type="text" v-model="newTicket.titolo" class="form-input"
                placeholder="Inserisci il titolo..." :required="!newTicket.motivazioneId" maxlength="100" />
              <div class="char-counter">{{ newTicket.titolo?.length || 0 }}/100</div>
            </div>

            <div class="form-group">
              <label class="form-label" for="motivazioneTicket">
                <i class="pi pi-list"></i> Motivazione Ticket <span class="opzionale">(opzionale)</span>
              </label>
              <select id="motivazioneTicket" v-model="newTicket.motivazioneId" class="form-select">
                <option value="">-- Seleziona motivazione --</option>
                <option v-for="m in motivazioniTicket" :key="m.id" :value="m.id">
                  {{ m.motivation }}
                </option>
              </select>
            </div>

            <div class="form-group">
              <label class="form-label" for="descrizioneTicket">
                <i class="pi pi-pencil"></i> Descrizione
              </label>
              <div v-if="motivazioneTicketTesto" class="descrizione-motivazione-prefix">
                Motivazione: {{ motivazioneTicketTesto }}
              </div>
              <textarea id="descrizioneTicket" v-model="newTicket.descrizione" class="form-textarea"
                :placeholder="newTicket.motivazioneId ? 'Descrivi il problema (opzionale)...' : 'Descrivi il problema...'"
                rows="4" :required="!newTicket.motivazioneId" maxlength="1000"></textarea>
              <div class="char-counter">{{ newTicket.descrizione?.length || 0 }}/1000</div>
            </div>

            <div class="form-group">
              <label class="form-label" for="prioritaTicket">
                <i class="pi pi-exclamation-triangle"></i> Priorità
              </label>
              <select id="prioritaTicket" v-model="newTicket.priorita" class="form-select">
                <option value="bassa">Bassa</option>
                <option value="media">Media</option>
                <option value="alta">Alta</option>
                <option value="urgente">Urgente</option>
              </select>
            </div>

            <div class="form-group">
              <label class="form-label" for="statoTicket">
                <i class="pi pi-flag"></i> Stato Ticket
              </label>
              <select id="statoTicket" v-model="newTicket.statusCode" class="form-select">
                <option value="opened">Aperto</option>
                <option value="closed">Chiuso</option>
              </select>
            </div>

            <div class="form-group">
              <label class="form-label" for="callTransferSuccess">
                <i class="pi pi-phone"></i> Trasferimento chiamata
              </label>
              <select
                id="callTransferSuccess"
                v-model="newTicket.callTransferSuccess"
                class="form-select"
                @change="onCallTransferSuccessChange"
              >
                <option :value="null">Non specificato</option>
                <option :value="true">Sì, trasferimento riuscito</option>
                <option :value="false">No, trasferimento non riuscito</option>
              </select>
            </div>

            <div class="form-group" v-if="requiresCallTransferArea(newTicket.callTransferSuccess)">
              <label class="form-label" for="callTransferArea">
                <i class="pi pi-directions-alt"></i> Area verso cui hai provato a trasferire
              </label>
              <select
                id="callTransferArea"
                v-model="newTicket.callTransferAreaId"
                class="form-select"
                required
              >
                <option :value="null" disabled>-- Seleziona area --</option>
                <option
                  v-for="area in ticketAreas"
                  :key="area.id_ticket_area"
                  :value="area.id_ticket_area"
                >
                  {{ area.area }} ({{ area.code }})
                </option>
              </select>
            </div>

            <div class="form-group">
              <div class="checkbox-group">
                <input type="checkbox" id="isRelated" v-model="newTicket.is_related" @change="onToggleIsRelated" />
                <label for="isRelated" class="checkbox-label">
                  Collegare il ticket a un pod,pdr,fattura...
                </label>
              </div>
            </div>
            <div class="form-group" v-if="newTicket.is_related">
              <label class="form-label">Tipo entità</label>
              <select v-model="linkEntityType" class="form-select" @change="onChangeEntityType">
                <option value="">-- Seleziona tipo --</option>
                <option v-for="t in entityTypes" :key="t.id" :value="t">
                  {{ t.label || t.type || t.name }}
                </option>
              </select>
            </div>

            <div class="form-group" v-if="newTicket.is_related && linkEntityType">
              <label class="form-label">Cerca</label>
              <div class="search-wrapper" style="position:relative; max-width: 100%;">
                <input class="form-input" v-model="entitySearch" @input="onSearchEntity" placeholder="Cerca..." />
              </div>
              <div v-if="showEntityList && entityOptions.length" class="entity-dropdown">
                <div
                  class="entity-item"
                  v-for="opt in entityOptions"
                  :key="opt.value || opt.id || opt.code || opt.label"
                  @click="selectEntity(opt)"
                >
                  {{ opt.label || opt.name || opt.code || opt.numero }} <small>({{ opt.type }})</small>
                </div>
              </div>
            </div>
            <div class="form-group selected-contact-info" v-if="entityId">
              <div class="info-header">
                <i class="pi pi-info-circle"></i>
                <span>Ticket per il contatto:</span>
              </div>
              <div class="contact-summary">
                <strong>{{ contactDisplayName }}</strong>
                <span class="contact-details" v-if="contact">
                  {{ contact.email }} - {{ contact.telefono }}
                </span>
              </div>
            </div>
          </form>
        </div>

        <div class="modal-footer">
          <button class="btn-secondary" @click="closeModal" type="button" :disabled="loading">
            <i class="pi pi-times"></i> Chiudi
          </button>
          <button class="btn-primary" @click="showReclamoConfirmation" type="button" :disabled="loading || !canCreateTicket">
            <i class="pi pi-spin pi-spinner" v-if="loading"></i>
            <i class="pi pi-check" v-else></i>
            {{ loading ? 'Creazione...' : 'Crea Ticket' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showTicketNotificationModal" class="modal-overlay" @click="closeTicketNotificationModal">
      <div class="modal-content modal-medium" @click.stop>
        <div class="modal-header">
          <h3>Notifica Email Ticket</h3>
          <button class="close-button" @click="closeTicketNotificationModal">&times;</button>
        </div>
        <div class="modal-body">
          <div v-if="loadingTicketNotificationPreview" class="ticket-email-preview-loading">
            <i class="pi pi-spin pi-spinner"></i>
            <span>Caricamento anteprima email...</span>
          </div>

          <div v-else-if="ticketNotificationPreview" class="ticket-email-preview">
            <div class="detail-row">
              <strong>Destinatario:</strong>
              <span>
                {{ ticketNotificationPreview.recipient_name || 'N/D' }}
                <template v-if="ticketNotificationPreview.to || ticketNotificationPreview.to_email">
                  ({{ ticketNotificationPreview.to || ticketNotificationPreview.to_email }})
                </template>
              </span>
            </div>
            <div class="detail-row">
              <strong>Oggetto:</strong>
              <span>{{ ticketNotificationPreview.subject || 'N/D' }}</span>
            </div>
            <div class="detail-row">
              <strong>Stato invio:</strong>
              <span :class="ticketNotificationPreview.can_send ? 'status-ok' : 'status-ko'">
                {{ ticketNotificationPreview.can_send ? 'Pronto all\'invio' : 'Invio non disponibile' }}
              </span>
            </div>
            <div class="ticket-email-preview-body">
              <strong>Anteprima:</strong>
              <pre>{{ ticketNotificationPreview.preview_text || ticketNotificationPreview.body || 'Nessun contenuto disponibile' }}</pre>
            </div>
          </div>

          <div v-else class="ticket-email-preview-empty">
            Anteprima non disponibile.
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="closeTicketNotificationModal" :disabled="sendingTicketNotificationEmail">
            Chiudi
          </button>
          <button
            class="btn-primary"
            type="button"
            @click="sendTicketNotification"
            :disabled="sendingTicketNotificationEmail || !canSendTicketNotification"
          >
            <i class="pi pi-spin pi-spinner" v-if="sendingTicketNotificationEmail"></i>
            <i class="pi pi-send" v-else></i>
            {{ sendingTicketNotificationEmail ? 'Invio...' : 'Invia Email' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showReclamoDialog" class="modal-overlay" @click="closeReclamoDialog">
      <div class="modal-content modal-small" @click.stop>
        <div class="modal-header">
          <h3>Conferma Creazione</h3>
          <button class="close-button" @click="closeReclamoDialog">&times;</button>
        </div>
        <div class="modal-body">
          <div class="message-content">
            <i class="pi pi-question-circle"></i>
            <p>Il ticket è stato creato con successo! Vuoi creare anche il reclamo?</p>
            <div v-if="showReclamoLink" class="reclamo-link-container" style="margin-top: 1rem; padding: 1rem; background-color: #f8f9fa; border-radius: 8px; border: 1px solid #dee2e6;">
              <a href="https://wizard.askmanagement.it/gestione-reclami" target="_blank" style="color: #007bff; text-decoration: none; word-break: break-all;">Pagina Reclami</a>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="createTicketWithoutReclamo">
            <i class="pi pi-times"></i> No, solo ticket
          </button>
          <button class="btn-primary" @click="createTicketWithReclamo">
            <i class="pi pi-check"></i> {{ showReclamoLink ? 'Chiudi e crea ticket' : 'Sì, crea anche reclamo' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="loading-overlay">
      <div class="loading-spinner">
        <i class="pi pi-spin pi-spinner"></i>
        <span>Caricamento...</span>
      </div>
    </div>

    <div class="tickets-table-container">
      <div class="fatture-table-header">
        <h3 class="section-title">Ticket</h3>
        <div class="table-actions">
          <button class="add-ticket-btn" @click="showAddTicketModal = true">
            <i class="pi pi-plus add-icon"></i>
            Nuovo Ticket
          </button>
        </div>
      </div>

      <div class="ticket-search-container" style="padding: 1rem; border-bottom: 1px solid #e0e0e0;">
        <div class="search-wrapper" style="position: relative; max-width: 400px;">
          <i class="pi pi-search" style="position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #666; font-size: 0.9rem;"></i>
          <input
            type="text"
            v-model="ticketSearchQuery"
            placeholder="Cerca per ID, titolo, stato..."
            @input="handleTicketSearch"
            style="width: 100%; padding: 8px 12px 8px 35px; border: 1px solid #ddd; border-radius: 4px; font-size: 0.9rem;"
          />
          <button
            v-if="ticketSearchQuery"
            @click="clearTicketSearch"
            style="position: absolute; right: 8px; top: 50%; transform: translateY(-50%); background: none; border: none; color: #999; cursor: pointer; padding: 4px;"
          >
            <i class="pi pi-times"></i>
          </button>
        </div>
        <div v-if="hasActiveTicketSearch" style="margin-top: 8px; font-size: 0.85rem; color: #666;">
          Trovati {{ filteredUserTickets.length }} ticket utente e {{ filteredSystemTickets.length }} ticket di sistema su {{ tickets.length }}
        </div>
      </div>

      <div class="tickets-subsection">
        <h4 class="tickets-subsection-title">Ticket utente</h4>
        <div class="fatture-table-wrapper">
          <table class="fatture-table">
            <thead>
              <tr>
                <th>Tipo</th>
                <th>ID</th>
                <th>Titolo</th>
                <th>Data</th>
                <th>Stato</th>
                <th>Destinatario</th>
                <th class="text-center">Azioni</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="ticket in paginatedUserTickets" :key="ticket.id" class="table-row">
                <td>
                  <span class="ticket-type" :class="getTicketTypeClass(ticket.tipo)">
                    {{ ticket.tipo || 'Ticket' }}
                  </span>
                </td>
                <td>{{ getTicketId(ticket) || 'N/A' }}</td>
                <td>{{ ticket.title || ticket.titolo }}</td>
                <td>{{ formatDate(ticket.created_at || ticket.data) }}</td>
                <td>
                  <span class="status-badge" :class="getStatusClass(ticket.status || ticket.stato)">
                    {{ ticket.status || ticket.stato }}
                  </span>
                </td>
                <td>{{ ticket.recipient_name || ticket.destinatario || 'N/A' }}</td>
                <td class="text-center">
                  <div class="fattura-actions">
                    <button class="action-btn view-btn" @click.stop="openTicketActions(ticket)" title="Apri ticket">
                      <i class="pi pi-eye table-icon"></i>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="filteredUserTickets.length === 0">
                <td colspan="7" class="no-data-message">
                  {{ hasActiveTicketSearch ? `Nessun ticket utente trovato per "${ticketSearchQuery}"` : 'Nessun ticket utente disponibile' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="pagination-container" v-if="totalPagesUserTickets > 1">
          <div class="pagination-info">
            Mostrando {{ userTicketStartIndex }} - {{ userTicketEndIndex }} di {{ filteredUserTickets.length }} ticket utente
            <span v-if="hasActiveTicketSearch" style="color: #666;">
              (filtrati da {{ userTicketsCount }} totali)
            </span>
          </div>
          <div class="pagination-controls">
            <button class="pagination-btn" @click="previousUserTicketsPage" :disabled="currentPage === 1">
              <i class="pi pi-chevron-left pagination-icon"></i>
            </button>
            <span class="page-indicator">{{ currentPage }} / {{ totalPagesUserTickets }}</span>
            <button class="pagination-btn" @click="nextUserTicketsPage" :disabled="currentPage === totalPagesUserTickets">
              <i class="pi pi-chevron-right pagination-icon"></i>
            </button>
          </div>
        </div>
      </div>

      <div class="tickets-subsection tickets-subsection-system">
        <h4 class="tickets-subsection-title">Ticket di sistema</h4>
        <div class="fatture-table-wrapper">
          <table class="fatture-table">
            <thead>
              <tr>
                <th>Tipo</th>
                <th>ID</th>
                <th>Titolo</th>
                <th>Data</th>
                <th>Stato</th>
                <th>Destinatario</th>
                <th class="text-center">Azioni</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="ticket in paginatedSystemTickets" :key="ticket.id" class="table-row">
                <td>
                  <span class="ticket-type" :class="getTicketTypeClass(ticket.tipo)">
                    {{ ticket.tipo || 'Ticket' }}
                  </span>
                </td>
                <td>{{ getTicketId(ticket) || 'N/A' }}</td>
                <td>{{ ticket.title || ticket.titolo }}</td>
                <td>{{ formatDate(ticket.created_at || ticket.data) }}</td>
                <td>
                  <span class="status-badge" :class="getStatusClass(ticket.status || ticket.stato)">
                    {{ ticket.status || ticket.stato }}
                  </span>
                </td>
                <td>{{ ticket.recipient_name || ticket.destinatario || 'N/A' }}</td>
                <td class="text-center">
                  <div class="fattura-actions">
                    <button class="action-btn view-btn" @click.stop="openTicket(ticket, 'view')" title="Visualizza">
                      <i class="pi pi-eye table-icon"></i>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="filteredSystemTickets.length === 0">
                <td colspan="7" class="no-data-message">
                  {{ hasActiveTicketSearch ? `Nessun ticket di sistema trovato per "${ticketSearchQuery}"` : 'Nessun ticket di sistema disponibile' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="pagination-container" v-if="totalPagesSystemTickets > 1">
          <div class="pagination-info">
            Mostrando {{ systemTicketStartIndex }} - {{ systemTicketEndIndex }} di {{ filteredSystemTickets.length }} ticket di sistema
            <span v-if="hasActiveTicketSearch" style="color: #666;">
              (filtrati da {{ systemTicketsCount }} totali)
            </span>
          </div>
          <div class="pagination-controls">
            <button class="pagination-btn" @click="previousSystemTicketsPage" :disabled="currentPageSystemTickets === 1">
              <i class="pi pi-chevron-left pagination-icon"></i>
            </button>
            <span class="page-indicator">{{ currentPageSystemTickets }} / {{ totalPagesSystemTickets }}</span>
            <button class="pagination-btn" @click="nextSystemTicketsPage" :disabled="currentPageSystemTickets === totalPagesSystemTickets">
              <i class="pi pi-chevron-right pagination-icon"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import services from '@/config/services'
import IncallService from '@/services/ask-management/Incall-service'
import TicketService from '@/services/ask-ticket/ticket-service'

export default {
  name: 'IncallTicketsBlock',

  props: {
    entityId: {
      type: [Number, String],
      required: true
    },
    entityType: {
      type: String,
      required: true
    },
    contact: {
      type: Object,
      default: null
    },
    relatedClients: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      ticketSearchQuery: '',
      ticketSearchTimeout: null,
      currentPage: 1,
      currentPageSystemTickets: 1,
      itemsPerPage: 10,
      loading: false,
      entityTypes: [],
      linkEntityType: '',
      entityOptions: [],
      selectedEntity: null,
      entitySearch: '',
      showEntityList: false,
      entitySearchTimeout: null,
      showTicketModal: false,
      showAddTicketModal: false,
      showTicketNotificationModal: false,
      showActionModal: false,
      showReclamoDialog: false,
      showReclamoLink: false,
      tickets: [],
      totalTickets: 0,
      selectedTicket: {},
      showTicketActionModal: false,
      ticketForAction: null,
      ticketModalMode: 'view',
      loadingTicketDetails: false,
      sendingTicketReply: false,
      ticketReplyMessage: '',
      ticketReplyCallTransferSuccess: null,
      ticketReplyCallTransferAreaId: null,
      ticketReplyStatusId: null,
      ticketReplyAssignType: '',
      ticketReplyAssignRecipient: null,
      ticketReplyAssignRecipients: [],
      ticketReplyAssignSearchQuery: '',
      showTicketReplyAssignSuggestions: false,
      loadingTicketReplyAssignSuggestions: false,
      ticketReplyAssignSearchTimeout: null,
      newTicket: {
        tipo: 'Ticket',
        tipoDestinatario: '',
        titolo: '',
        descrizione: '',
        priorita: 'media',
        stato: 'In Attesa',
        statusCode: 'opened',
        recipient: null,
        recipient_id: null,
        area_id: 1,
        is_related: false,
        motivazioneId: null,
        callTransferSuccess: null,
        callTransferAreaId: null,
      },
      incallService: null,
      ticketService: null,
      typesRecipients: [
        { name: 'agente', code: 'Agente' },
        { name: 'client', code: 'Cliente' },
        { name: 'rappresentante', code: 'Rappresentante' },
        { name: 'assicuratori', code: 'Assicuratore' },
        { name: 'user', code: 'User' },
        { name: 'area', code: 'Area User' },
      ],
      recipients: [],
      ticketAreas: [],
      ticketStatuses: [],
      motivazioniTicket: [],
      recipientSearchQuery: '',
      showRecipientSuggestions: false,
      loadingRecipientSuggestions: false,
      recipientSearchTimeout: null,
      creatingTicketData: null,
      ticketNotificationPreview: null,
      loadingTicketNotificationPreview: false,
      sendingTicketNotificationEmail: false,
      ticketModalDrag: { active: false, startX: 0, startY: 0, deltaX: 0, deltaY: 0 },
      backendConfig: null,
    }
  },

  computed: {
    ticketModalPositionStyle() {
      const d = this.ticketModalDrag
      if (!d || (d.deltaX === 0 && d.deltaY === 0)) return {}
      return {
        transform: `translate(${d.deltaX}px, ${d.deltaY}px)`
      }
    },

    filteredTickets() {
      if (!this.ticketSearchQuery.trim()) {
        return this.tickets
      }

      const query = this.ticketSearchQuery.toLowerCase().trim()

      return this.tickets.filter(ticket => {
        const ticketId = String(this.getTicketId(ticket) ?? '').toLowerCase()
        const title = (ticket.title || ticket.titolo || '').toLowerCase()
        const status = (ticket.status || ticket.stato || '').toLowerCase()
        const recipient = (ticket.recipient_name || ticket.destinatario || '').toLowerCase()
        const tipo = (ticket.tipo || '').toLowerCase()

        return ticketId.includes(query) ||
               title.includes(query) ||
               status.includes(query) ||
               recipient.includes(query) ||
               tipo.includes(query)
      })
    },

    userTicketsCount() {
      return this.tickets.filter(ticket => !this.isSystemTicket(ticket)).length
    },

    systemTicketsCount() {
      return this.tickets.filter(ticket => this.isSystemTicket(ticket)).length
    },

    filteredUserTickets() {
      return this.filteredTickets.filter(ticket => !this.isSystemTicket(ticket))
    },

    filteredSystemTickets() {
      return this.filteredTickets.filter(ticket => this.isSystemTicket(ticket))
    },

    totalPagesUserTickets() {
      return Math.ceil(this.filteredUserTickets.length / this.itemsPerPage)
    },

    totalPagesSystemTickets() {
      return Math.ceil(this.filteredSystemTickets.length / this.itemsPerPage)
    },

    userTicketStartIndex() {
      if (this.filteredUserTickets.length === 0) return 0
      return (this.currentPage - 1) * this.itemsPerPage + 1
    },

    userTicketEndIndex() {
      return Math.min(this.currentPage * this.itemsPerPage, this.filteredUserTickets.length)
    },

    systemTicketStartIndex() {
      if (this.filteredSystemTickets.length === 0) return 0
      return (this.currentPageSystemTickets - 1) * this.itemsPerPage + 1
    },

    systemTicketEndIndex() {
      return Math.min(this.currentPageSystemTickets * this.itemsPerPage, this.filteredSystemTickets.length)
    },

    paginatedUserTickets() {
      if (this.filteredUserTickets.length === 0) return []
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredUserTickets.slice(start, end)
    },

    paginatedSystemTickets() {
      if (this.filteredSystemTickets.length === 0) return []
      const start = (this.currentPageSystemTickets - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredSystemTickets.slice(start, end)
    },

    selectedTicketMessages() {
      const messages = this.selectedTicket?.messages
      if (!messages || typeof messages !== 'object') return []

      const grouped = []
      Object.keys(messages).sort().forEach((date) => {
        const dayMessages = Array.isArray(messages[date]) ? messages[date] : []
        dayMessages.forEach((message) => {
          grouped.push({ ...message, date })
        })
      })
      return grouped
    },

    selectedTicketHistoryStatus() {
      return Array.isArray(this.selectedTicket?.history_status) ? this.selectedTicket.history_status : []
    },

    selectedTicketHistoryAssignment() {
      return Array.isArray(this.selectedTicket?.history_assignment) ? this.selectedTicket.history_assignment : []
    },

    currentUserId() {
      try {
        if (!this.$store?.state?.auth?.user) return null
        const user = JSON.parse(atob(this.$store.state.auth.user))
        return user?.user?.[0]?.id ?? null
      } catch {
        return null
      }
    },

    showTicketReplyForm() {
      return this.ticketModalMode === 'reply' && !this.isSystemTicket(this.selectedTicket)
    },

    hasTicketReplyMessage() {
      return this.ticketReplyMessage?.trim().length > 0
    },

    hasTicketReplyAssignmentSelection() {
      return !!this.ticketReplyAssignType && !!this.ticketReplyAssignRecipient
    },

    hasTicketReplyStatusChange() {
      return this.shouldUpdateTicketReplyStatus(this.getTicketCurrentStatusId(this.selectedTicket))
    },

    canSubmitTicketReplyForm() {
      return this.hasTicketReplyMessage || this.hasTicketReplyAssignmentSelection || this.hasTicketReplyStatusChange
    },

    ticketReplySubmitLabel() {
      if (this.hasTicketReplyMessage) return 'Invia risposta'
      if (this.hasTicketReplyAssignmentSelection && this.hasTicketReplyStatusChange) return 'Salva assegnazione e stato'
      if (this.hasTicketReplyAssignmentSelection) return 'Salva assegnazione'
      return 'Salva stato'
    },

    canCreateTicket() {
      const hasMotivazione = !!this.newTicket.motivazioneId
      const hasTitolo = !!this.newTicket.titolo?.trim()
      const hasDescrizione = !!this.newTicket.descrizione?.trim()
      const contentOk = hasMotivazione || (hasTitolo && hasDescrizione)
      return this.newTicket.tipoDestinatario &&
        contentOk &&
        !!this.entityId &&
        this.newTicket.recipient
    },

    motivazioneTicketTesto() {
      if (!this.newTicket.motivazioneId) return ''
      const m = this.motivazioniTicket.find(x => String(x.id) === String(this.newTicket.motivazioneId))
      return m?.motivation || ''
    },

    canSendTicketNotification() {
      return !!(
        this.creatingTicketData?.ticketId &&
        this.ticketNotificationPreview?.can_send
      )
    },

    hasActiveTicketSearch() {
      return this.ticketSearchQuery.trim().length > 0
    },

    contactDisplayName() {
      return this.getContactDisplayName()
    },

    showRecipientSearch() {
      const tipo = this.newTicket.tipoDestinatario
      return !!(tipo && tipo !== 'area' && !this.shouldAutoAssignCurrentRecipient(tipo))
    },

    usesLocalRecipientFilter() {
      return (
        this.newTicket.tipoDestinatario === 'client' &&
        this.entityType === 'rappresentante' &&
        (this.relatedClients || []).length > 0
      )
    },

    recipientSearchEmptyMessage() {
      const query = this.recipientSearchQuery.trim()
      if (this.usesLocalRecipientFilter) {
        return query ? 'Nessun destinatario trovato' : 'Digita per filtrare la lista clienti'
      }
      if (query.length < 2) {
        return 'Digita almeno 2 caratteri per cercare'
      }
      return 'Nessun destinatario trovato'
    },

    ticketReplyAssignEmptyMessage() {
      const query = this.ticketReplyAssignSearchQuery.trim()
      if (query.length < 2) {
        return 'Digita almeno 2 caratteri per cercare'
      }
      return 'Nessun destinatario trovato'
    }
  },

  watch: {
    entityId() {
      this.loadAllTickets()
    },
    entityType() {
      this.loadAllTickets()
    }
  },

  async mounted() {
    this.incallService = new IncallService(this.$http, this.$eventBus)
    this.ticketService = new TicketService(this.$http, this.$eventBus)
    await this.loadTicketOptions()
    await this.loadAllTickets()
  },

  beforeUnmount() {
    this.endTicketModalDrag()
    if (this.ticketSearchTimeout) clearTimeout(this.ticketSearchTimeout)
    if (this.entitySearchTimeout) clearTimeout(this.entitySearchTimeout)
    if (this.recipientSearchTimeout) clearTimeout(this.recipientSearchTimeout)
    if (this.ticketReplyAssignSearchTimeout) clearTimeout(this.ticketReplyAssignSearchTimeout)
  },

  methods: {
    async onToggleIsRelated() {
      if (this.newTicket.is_related) {
        try {
          const res = await this.incallService.getTypesEntity()
          this.entityTypes = res.items || []
        } catch (e) {
          this.entityTypes = []
        }
      } else {
        this.linkEntityType = ''
        this.entityOptions = []
        this.selectedEntity = null
        this.entitySearch = ''
        this.showEntityList = false
      }
    },

    async onChangeEntityType() {
      this.selectedEntity = null
      this.entitySearch = ''
      this.entityOptions = []
      if (!this.linkEntityType) return
      await this.loadEntityOptions()
    },

    async loadEntityOptions() {
      if (!this.linkEntityType) return
      try {
        const { items } = await this.incallService.getTicketEntity(
          this.entitySearch || '',
          this.linkEntityType,
          { limit: 10, offset: 0 }
        )
        this.entityOptions = Array.isArray(items) ? items : []
        this.showEntityList = true
      } catch (e) {
        this.entityOptions = []
        this.showEntityList = false
      }
    },

    onSearchEntity() {
      if (this.entitySearchTimeout) clearTimeout(this.entitySearchTimeout)
      this.entitySearchTimeout = setTimeout(() => {
        this.loadEntityOptions()
      }, 300)
    },

    selectEntity(opt) {
      this.selectedEntity = { id: (opt.value || opt.id), label: (opt.label || opt.name || opt.code || opt.numero), type: opt.type }
      this.entitySearch = this.selectedEntity.label
      this.showEntityList = false
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleDateString('it-IT')
    },

    handleApiError(error, defaultMessage = 'Errore imprevisto') {
      const message = error.message || defaultMessage

      if (this.$notify) {
        this.$notify.error({
          title: 'Errore',
          message: message
        })
      } else if (this.$message) {
        this.$message.error(message)
      } else {
        alert(message)
      }
    },

    async loadTicketOptions() {
      try {
        const areasResponse = await this.incallService.getTypesAreaTicket()
        if (areasResponse.items) {
          this.ticketAreas = areasResponse.items
        }

        const statusResponse = await this.incallService.getStatus()
        if (statusResponse.items) {
          this.ticketStatuses = statusResponse.items
        }

        const motivazioniResponse = await this.incallService.getMotivationTicket()
        if (motivazioniResponse.items && Array.isArray(motivazioniResponse.items)) {
          this.motivazioniTicket = motivazioniResponse.items
        }
      } catch (error) {
        // Errore caricamento opzioni ticket
      }
    },

    async loadAllTickets() {
      try {
        let response

        if (this.entityId && this.entityType) {
          response = await this.getUserTickets(this.entityId, this.entityType)
        } else {
          response = await this.incallService.getTickets()
        }

        let tickets = []
        if (response.success && response.data && Array.isArray(response.data)) {
          tickets = response.data
        } else if (response.items && response.items.success && response.items.data && Array.isArray(response.items.data)) {
          tickets = response.items.data
        } else if (response.items && Array.isArray(response.items)) {
          tickets = response.items
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          tickets = response.data.data
        } else if (response.data && Array.isArray(response.data)) {
          tickets = response.data
        }

        this.tickets = tickets
        this.totalTickets = tickets.length
        this.currentPage = 1
        this.currentPageSystemTickets = 1
        this.ticketSearchQuery = ''
      } catch (error) {
        this.tickets = []
        this.totalTickets = 0
      }
    },

    async getUserTickets(userId, userType) {
      let targetUserType = userType
      let resolvedUserId = userId
      if (String(resolvedUserId).startsWith('rappresentante_')) {
        targetUserType = 'rappresentante'
        resolvedUserId = String(resolvedUserId).replace('rappresentante_', '')
      } else if (userType === 'client' || !userType) {
        targetUserType = 'client'
      }

      return await this.incallService.getUserTickets(resolvedUserId, targetUserType)
    },

    async createTicketDirect(ticketData) {
      if (this.backendConfig) {
        const endpoint = this.backendConfig.endpoint.replace('/search/contatti', '/ticket/create')
        const url = `${this.backendConfig.baseUrl}${endpoint}`

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...(this.$http?.defaults?.headers?.common?.Authorization && {
              'Authorization': this.$http.defaults.headers.common.Authorization
            })
          },
          body: JSON.stringify({ ticket: ticketData })
        })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const data = await response.json()
        return data
      }

      const response = await this.$http.post(`${services.ask_pratiche.url}/incall/ticket/create`, {
        ticket: ticketData
      })

      return response.data
    },

    async onChangeTypeRecipient() {
      this.newTicket.recipient = null
      this.recipients = []
      this.recipientSearchQuery = ''
      this.showRecipientSuggestions = false
      this.loadingRecipientSuggestions = false

      if (this.recipientSearchTimeout) {
        clearTimeout(this.recipientSearchTimeout)
      }

      const tipo = this.newTicket.tipoDestinatario
      if (!tipo || tipo === 'area') {
        return
      }

      const shouldAutoAssign = this.shouldAutoAssignCurrentRecipient(tipo)
      if (shouldAutoAssign) {
        this.preselectCurrentContactRecipient()
        await this.fetchRecipientsForType(tipo, '', true)
        return
      }

      if (this.usesLocalRecipientFilter) {
        await this.fetchRecipientsForType(tipo, '', false)
      }
    },

    recipientTypeLabel(type) {
      const found = this.typesRecipients.find((item) => item.name === type)
      return found?.code || type
    },

    openRecipientSuggestions() {
      const tipo = this.newTicket.tipoDestinatario
      if (!tipo || tipo === 'area' || !this.showRecipientSearch) {
        return
      }

      this.showRecipientSuggestions = true

      if (this.usesLocalRecipientFilter || this.recipientSearchQuery.trim().length >= 2) {
        this.fetchRecipientsForType(tipo, this.recipientSearchQuery.trim(), false)
      }
    },

    closeRecipientSuggestions() {
      setTimeout(() => {
        this.showRecipientSuggestions = false
      }, 200)
    },

    selectRecipientSuggestion(recipient) {
      this.newTicket.recipient = recipient
      this.recipientSearchQuery = recipient.label
      this.showRecipientSuggestions = false
    },

    clearSelectedRecipient() {
      this.newTicket.recipient = null
      this.recipientSearchQuery = ''
      this.recipients = []
      this.showRecipientSuggestions = false
    },

    handleRecipientSearch() {
      const tipo = this.newTicket.tipoDestinatario
      if (!tipo || tipo === 'area' || this.shouldAutoAssignCurrentRecipient(tipo)) {
        return
      }

      const query = this.recipientSearchQuery.trim()
      if (
        this.newTicket.recipient &&
        query !== String(this.newTicket.recipient.label || '').trim()
      ) {
        this.newTicket.recipient = null
      }

      this.showRecipientSuggestions = true

      if (this.recipientSearchTimeout) {
        clearTimeout(this.recipientSearchTimeout)
      }

      if (this.usesLocalRecipientFilter) {
        this.recipients = this.filterRecipientsByQuery(
          this.mapRelatedClientsToRecipients(),
          query
        )
        return
      }

      if (query.length < 2) {
        this.recipients = []
        return
      }

      this.recipientSearchTimeout = setTimeout(async () => {
        await this.fetchRecipientsForType(tipo, query, false)
      }, 300)
    },

    buildRecipientRequestOptions(tipo) {
      const shouldAutoAssign = this.shouldAutoAssignCurrentRecipient(tipo)
      const options = {
        id: shouldAutoAssign ? (this.getCurrentContactRecipientId(tipo) || undefined) : undefined,
      }

      if (tipo === 'client' && this.entityType === 'rappresentante') {
        options.rappresentante_id = Number(this.entityId || this.contact?.id) || undefined
      }

      return options
    },

    mapRelatedClientsToRecipients() {
      return (this.relatedClients || []).map((client) => ({
        id: client.id,
        label: client.nome || `Cliente #${client.id}`,
        type: 'client',
      }))
    },

    filterRecipientsByQuery(recipients, query) {
      const normalizedQuery = String(query || '').trim().toLowerCase()
      if (!normalizedQuery) {
        return recipients
      }

      return recipients.filter((recipient) =>
        String(recipient.label || '').toLowerCase().includes(normalizedQuery)
      )
    },

    async fetchRecipientsForType(tipo, searchString = '', shouldAutoAssign = false) {
      const query = String(searchString || '').trim()

      if (tipo === 'client' && this.entityType === 'rappresentante') {
        const relatedRecipients = this.mapRelatedClientsToRecipients()
        if (relatedRecipients.length > 0) {
          this.recipients = this.filterRecipientsByQuery(relatedRecipients, query)
          this.showRecipientSuggestions = !shouldAutoAssign
          return
        }
      }

      if (!shouldAutoAssign && query.length < 2) {
        this.recipients = []
        return
      }

      this.loadingRecipientSuggestions = true
      try {
        const response = await this.incallService.getTicketRecipients(
          query,
          tipo,
          this.buildRecipientRequestOptions(tipo),
          { silent: true }
        )
        const items = Array.isArray(response.items) ? response.items : []
        this.recipients = items
        this.showRecipientSuggestions = !shouldAutoAssign

        if (shouldAutoAssign) {
          this.preselectCurrentContactRecipient()
        }
      } catch (error) {
        this.recipients = []
        if (shouldAutoAssign && !this.newTicket.recipient) {
          this.preselectCurrentContactRecipient()
        }
      } finally {
        this.loadingRecipientSuggestions = false
      }
    },

    shouldAutoAssignCurrentRecipient(tipoDestinatario) {
      if (!tipoDestinatario) return false
      if (tipoDestinatario === 'client' && this.entityType === 'client') return true
      if (tipoDestinatario === 'rappresentante' && this.entityType === 'rappresentante') return true
      return false
    },

    getRecipientSearchSeed() {
      if (!this.contact) return ''

      return (
        this.contact.ragsoc
        || [this.contact.cliente_nome, this.contact.cliente_cognome].filter(Boolean).join(' ')
        || [this.contact.nome, this.contact.cognome].filter(Boolean).join(' ')
        || this.contact.nome
        || ''
      ).trim()
    },

    getCurrentContactRecipientId(tipoDestinatario) {
      if (!this.contact?.id) return null

      if (tipoDestinatario === 'client' && this.entityType === 'client') {
        return Number(this.entityId || this.contact.client_id || this.contact.id) || null
      }

      if (tipoDestinatario === 'rappresentante' && this.entityType === 'rappresentante') {
        return Number(this.entityId || this.contact.id) || null
      }

      return null
    },

    buildRecipientFromCurrentContact(tipoDestinatario) {
      const targetId = this.getCurrentContactRecipientId(tipoDestinatario)
      if (!targetId) return null

      if (tipoDestinatario === 'client') {
        const label = this.contact.ragsoc
          || [this.contact.cliente_nome, this.contact.cliente_cognome].filter(Boolean).join(' ')
          || [this.contact.nome, this.contact.cognome].filter(Boolean).join(' ')
          || this.contact.nome
          || `Cliente #${targetId}`

        return {
          id: targetId,
          label,
          type: 'client',
        }
      }

      if (tipoDestinatario === 'rappresentante') {
        const label = this.contact.ragsoc
          || this.contact.nome
          || [this.contact.nome, this.contact.cognome].filter(Boolean).join(' ')
          || `Rappresentante #${targetId}`

        return {
          id: targetId,
          label,
          type: 'rappresentante',
        }
      }

      return null
    },

    preselectCurrentContactRecipient() {
      const tipo = this.newTicket.tipoDestinatario
      if (!tipo || tipo === 'area') return

      const targetId = this.getCurrentContactRecipientId(tipo)
      if (!targetId) return

      const recipients = Array.isArray(this.recipients) ? this.recipients : []
      let match = recipients.find((recipient) => Number(recipient.id) === targetId)

      if (!match) {
        match = this.buildRecipientFromCurrentContact(tipo)
        if (match) {
          this.recipients = [match, ...recipients.filter((recipient) => Number(recipient.id) !== targetId)]
        }
      }

      if (match) {
        this.newTicket.recipient = match
      }
    },

    openTicketActions(ticket) {
      this.ticketForAction = ticket
      this.showTicketActionModal = true
    },

    closeTicketActionModal() {
      this.showTicketActionModal = false
      this.ticketForAction = null
    },

    selectTicketAction(mode) {
      const ticket = this.ticketForAction
      this.closeTicketActionModal()
      if (ticket) {
        this.openTicket(ticket, mode)
      }
    },

    async openTicket(ticket, mode = 'view') {
      this.selectedTicket = { ...ticket }
      this.ticketModalMode = mode
      this.resetTicketReplyForm()
      this.showTicketModal = true

      if (!this.ticketHasEmbeddedDetails(ticket)) {
        await this.loadSelectedTicketDetails(ticket)
      } else {
        this.$nextTick(() => this.scrollTicketMessagesToBottom())
      }

      if (mode === 'reply') {
        this.initTicketReplyStatus(this.selectedTicket)
      }
    },

    ticketHasEmbeddedDetails(ticket) {
      if (!ticket) return false
      const hasMessages = ticket.messages && typeof ticket.messages === 'object' && Object.keys(ticket.messages).length > 0
      const hasHistory = Array.isArray(ticket.history_status) && ticket.history_status.length > 0
      return hasMessages || hasHistory
    },

    syncSelectedTicketFromList() {
      const ticketId = this.getTicketId(this.selectedTicket)
      if (!ticketId) return

      const updatedTicket = this.tickets.find((ticket) => this.getTicketId(ticket) === ticketId)
      if (updatedTicket) {
        this.selectedTicket = { ...updatedTicket }
      }
    },

    async loadSelectedTicketDetails(ticket) {
      this.loadingTicketDetails = true
      try {
        const { item } = await this.incallService.getTicketDetails(ticket)
        if (item) {
          this.selectedTicket = { ...ticket, ...item }
          if (this.ticketModalMode === 'reply') {
            this.initTicketReplyStatus(this.selectedTicket)
          }
        }
      } catch (error) {
        this.handleApiError(error, 'Errore nel caricamento della conversazione')
      } finally {
        this.loadingTicketDetails = false
        this.$nextTick(() => this.scrollTicketMessagesToBottom())
      }
    },

    getTicketId(ticket) {
      return ticket?.id_ticket ?? ticket?.id ?? null
    },

    isOwnTicketMessage(message) {
      return this.currentUserId != null && message?.id_user == this.currentUserId
    },

    parseTicketDate(value) {
      if (!value) return null
      const normalized = String(value).includes('T') ? value : String(value).replace(' ', 'T')
      const date = new Date(normalized)
      return Number.isNaN(date.getTime()) ? null : date
    },

    formatTicketMessageTime(message) {
      const value = message?.datetime || message?.date
      const date = this.parseTicketDate(value)
      if (!date) return value || ''
      return date.toLocaleString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    formatTicketHistoryDate(value) {
      const date = this.parseTicketDate(value)
      if (!date) return value || 'N/A'
      return date.toLocaleString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    messageAttachments(message) {
      const list = message?.attachments
      if (Array.isArray(list)) return list
      if (list && typeof list === 'object') return Object.values(list)
      return []
    },

    attachmentKey(attachment) {
      return attachment?.code || attachment?.mail_attachment_id || attachment?.name || attachment?.path
    },

    openTicketAttachment(attachment) {
      this.ticketService?.openAttachment(attachment)
    },

    downloadTicketAttachment(attachment) {
      this.ticketService?.downloadAttachment(attachment)
    },

    getTicketMessageSender(message) {
      return message?.sender || message?.name || this.selectedTicket?.creator_name || this.selectedTicket?.creator || 'Sistema'
    },

    getCallTransferSuccess(item) {
      if (!item) return null

      if (item.call_transfer_success !== undefined && item.call_transfer_success !== null && item.call_transfer_success !== '') {
        return item.call_transfer_success === true
          || item.call_transfer_success === 1
          || item.call_transfer_success === '1'
      }

      if (item.call_transfer?.success !== undefined && item.call_transfer?.success !== null && item.call_transfer?.success !== '') {
        return !!item.call_transfer.success
      }

      return null
    },

    getCallTransferAreaLabel(item) {
      if (!item) return ''

      return item.call_transfer_area_name
        || item.call_transfer_area
        || item.area_call_transfer
        || this.getTicketAreaNameById(item.id_call_transfer_area)
        || this.getTicketAreaNameById(item.call_transfer?.id_ticket_area)
        || ''
    },

    getTicketAreaNameById(areaId) {
      if (!areaId) return ''
      const area = this.ticketAreas.find((entry) => entry.id_ticket_area === areaId)
      if (!area) return ''
      return area.area || area.code || ''
    },

    hasCallTransferInfo(item) {
      return this.getCallTransferSuccess(item) !== null
    },

    getTicketCallTransferLabel(item) {
      const success = this.getCallTransferSuccess(item)
      if (success === null) return ''

      const area = this.getCallTransferAreaLabel(item)
      if (success) {
        return area ? `Trasferimento riuscito verso ${area}` : 'Trasferimento riuscito'
      }

      return area ? `Trasferimento non riuscito verso ${area}` : 'Trasferimento non riuscito'
    },

    getCallTransferBadgeClass(item) {
      const success = this.getCallTransferSuccess(item)
      if (success === true) return 'call-transfer-badge call-transfer-badge--success'
      if (success === false) return 'call-transfer-badge call-transfer-badge--failed'
      return 'call-transfer-badge'
    },

    resetTicketReplyCallTransfer() {
      this.ticketReplyCallTransferSuccess = null
      this.ticketReplyCallTransferAreaId = null
    },

    resetTicketReplyAssignment() {
      this.ticketReplyAssignType = ''
      this.ticketReplyAssignRecipient = null
      this.ticketReplyAssignRecipients = []
      this.ticketReplyAssignSearchQuery = ''
      this.showTicketReplyAssignSuggestions = false
      this.loadingTicketReplyAssignSuggestions = false
      if (this.ticketReplyAssignSearchTimeout) {
        clearTimeout(this.ticketReplyAssignSearchTimeout)
        this.ticketReplyAssignSearchTimeout = null
      }
    },

    resetTicketReplyForm() {
      this.ticketReplyMessage = ''
      this.resetTicketReplyCallTransfer()
      this.resetTicketReplyAssignment()
      this.ticketReplyStatusId = null
    },

    onTicketReplyAssignTypeChange() {
      this.ticketReplyAssignRecipient = null
      this.ticketReplyAssignRecipients = []
      this.ticketReplyAssignSearchQuery = ''
      this.showTicketReplyAssignSuggestions = false
      this.loadingTicketReplyAssignSuggestions = false
      if (this.ticketReplyAssignSearchTimeout) {
        clearTimeout(this.ticketReplyAssignSearchTimeout)
        this.ticketReplyAssignSearchTimeout = null
      }
    },

    openTicketReplyAssignSuggestions() {
      if (!this.ticketReplyAssignType || this.ticketReplyAssignType === 'area') return
      this.showTicketReplyAssignSuggestions = true
      if (this.ticketReplyAssignSearchQuery.trim().length >= 2) {
        this.fetchTicketReplyAssignRecipients(this.ticketReplyAssignSearchQuery.trim())
      }
    },

    closeTicketReplyAssignSuggestions() {
      setTimeout(() => {
        this.showTicketReplyAssignSuggestions = false
      }, 200)
    },

    handleTicketReplyAssignSearch() {
      if (!this.ticketReplyAssignType || this.ticketReplyAssignType === 'area') return

      const query = this.ticketReplyAssignSearchQuery.trim()
      if (
        this.ticketReplyAssignRecipient &&
        query !== String(this.ticketReplyAssignRecipient.label || '').trim()
      ) {
        this.ticketReplyAssignRecipient = null
      }

      this.showTicketReplyAssignSuggestions = true

      if (this.ticketReplyAssignSearchTimeout) {
        clearTimeout(this.ticketReplyAssignSearchTimeout)
      }

      if (query.length < 2) {
        this.ticketReplyAssignRecipients = []
        return
      }

      this.ticketReplyAssignSearchTimeout = setTimeout(async () => {
        await this.fetchTicketReplyAssignRecipients(query)
      }, 300)
    },

    async fetchTicketReplyAssignRecipients(searchString = '') {
      const query = String(searchString || '').trim()
      if (!this.ticketReplyAssignType || this.ticketReplyAssignType === 'area' || query.length < 2) {
        this.ticketReplyAssignRecipients = []
        return
      }

      this.loadingTicketReplyAssignSuggestions = true
      try {
        const response = await this.incallService.getTicketRecipients(
          query,
          this.ticketReplyAssignType,
          {},
          { silent: true }
        )
        this.ticketReplyAssignRecipients = Array.isArray(response.items) ? response.items : []
        this.showTicketReplyAssignSuggestions = true
      } catch (error) {
        this.ticketReplyAssignRecipients = []
      } finally {
        this.loadingTicketReplyAssignSuggestions = false
      }
    },

    selectTicketReplyAssignRecipient(recipient) {
      this.ticketReplyAssignRecipient = recipient
      this.ticketReplyAssignSearchQuery = recipient.label
      this.showTicketReplyAssignSuggestions = false
    },

    clearTicketReplyAssignRecipient() {
      this.ticketReplyAssignRecipient = null
      this.ticketReplyAssignSearchQuery = ''
      this.ticketReplyAssignRecipients = []
      this.showTicketReplyAssignSuggestions = false
    },

    resolveTicketAssignRecipientId(recipient, type) {
      if (!recipient) return null
      if (type === 'user') {
        return recipient.id_user ?? recipient.user_id ?? recipient.idUser ?? recipient.id ?? null
      }
      return recipient.id ?? recipient.id_user ?? recipient.user_id ?? null
    },

    buildTicketReplyAssignPayload() {
      if (!this.ticketReplyAssignType) return null

      if (this.ticketReplyAssignType === 'area') {
        const area = this.ticketReplyAssignRecipient
        if (!area) return null
        return {
          id: null,
          type: 'area',
          id_ticket_area: area.id_ticket_area,
        }
      }

      const recipient = this.ticketReplyAssignRecipient
      if (!recipient) return null
      const recipientId = this.resolveTicketAssignRecipientId(recipient, this.ticketReplyAssignType)
      if (!recipientId) return null
      return {
        id: recipientId,
        type: recipient.type || this.ticketReplyAssignType,
        id_ticket_area: recipient.id_ticket_area || 0,
      }
    },

    getSelectedTicketAssignmentLabel() {
      if (!this.selectedTicket) return ''
      if (this.selectedTicket.assigned_user_name) return this.selectedTicket.assigned_user_name
      if (this.selectedTicket.assigned_name) return this.selectedTicket.assigned_name
      if (this.selectedTicket.id_assigned_user) {
        return `${this.recipientTypeLabel(this.selectedTicket.assigned_user_type)} #${this.selectedTicket.id_assigned_user}`
      }
      return ''
    },

    findTicketStatusByLabel(label) {
      if (!label || !Array.isArray(this.ticketStatuses)) {
        return null
      }

      const normalized = String(label).toLowerCase().trim()
      return this.ticketStatuses.find((entry) => (
        String(entry.status || '').toLowerCase().trim() === normalized
      )) || null
    },

    getTicketCurrentStatusId(ticket) {
      if (!ticket) {
        return null
      }

      if (ticket.id_ticket_status != null && ticket.id_ticket_status !== '') {
        return ticket.id_ticket_status
      }

      const history = ticket.history_status
      if (Array.isArray(history) && history.length > 0) {
        const last = history[history.length - 1]
        if (last?.id_ticket_status != null && last.id_ticket_status !== '') {
          return last.id_ticket_status
        }

        const historyMatch = this.findTicketStatusByLabel(last?.status)
        if (historyMatch) {
          return historyMatch.id_ticket_status
        }
      }

      const currentMatch = this.findTicketStatusByLabel(ticket.status || ticket.stato)
      return currentMatch?.id_ticket_status ?? null
    },

    initTicketReplyStatus(ticket) {
      const currentStatusId = this.getTicketCurrentStatusId(ticket)
      this.ticketReplyStatusId = currentStatusId
    },

    shouldUpdateTicketReplyStatus(previousStatusId) {
      if (this.ticketReplyStatusId == null || this.ticketReplyStatusId === '') {
        return false
      }

      if (previousStatusId == null || previousStatusId === '') {
        return true
      }

      return Number(this.ticketReplyStatusId) !== Number(previousStatusId)
    },

    onTicketReplyCallTransferSuccessChange() {
      if (!this.requiresCallTransferArea(this.ticketReplyCallTransferSuccess)) {
        this.ticketReplyCallTransferAreaId = null
      }
    },

    buildReplyCallTransferPayload() {
      if (!this.requiresCallTransferArea(this.ticketReplyCallTransferSuccess)) {
        return null
      }

      return {
        success: this.ticketReplyCallTransferSuccess === true,
        id_ticket_area: this.ticketReplyCallTransferAreaId,
      }
    },

    formatTicketMessageContent(content) {
      if (!content) return ''
      return String(content).replace(/\n/g, '<br>')
    },

    scrollTicketMessagesToBottom() {
      const container = this.$refs.ticketMessagesContainer
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    },

    getTicketReplySuccessMessage(messageSent, statusChanged, assigned) {
      const parts = []
      if (messageSent) parts.push('risposta inviata')
      if (statusChanged) parts.push('stato aggiornato')
      if (assigned) parts.push('ticket assegnato')

      if (parts.length === 0) return 'Ticket aggiornato'
      return parts.join(', ').replace(/^./, (char) => char.toUpperCase())
    },

    async refreshSelectedTicketAfterAction(ticketId) {
      await this.loadAllTickets()

      const updatedTicket = this.tickets.find((ticket) => this.getTicketId(ticket) === ticketId)
      let freshDetails = null

      try {
        const { item } = await this.incallService.getTicketDetails({ id_ticket: ticketId })
        freshDetails = item || null
      } catch (error) {
        freshDetails = null
      }

      if (updatedTicket || freshDetails) {
        this.selectedTicket = {
          ...this.selectedTicket,
          ...(updatedTicket || {}),
          ...(freshDetails || {}),
        }
      }

      if (this.ticketModalMode === 'reply') {
        this.initTicketReplyStatus(this.selectedTicket)
      }

      this.$eventBus?.emit('update-numbers-ticket')
      this.$nextTick(() => this.scrollTicketMessagesToBottom())
    },

    async sendTicketReply() {
      const message = this.ticketReplyMessage?.trim()

      const ticketId = this.getTicketId(this.selectedTicket)
      if (!ticketId) {
        this.handleApiError({ message: 'ID ticket non valido' })
        return
      }

      const assignPayload = this.buildTicketReplyAssignPayload()
      const previousStatusId = this.getTicketCurrentStatusId(this.selectedTicket)
      const shouldChangeStatus = this.shouldUpdateTicketReplyStatus(previousStatusId)

      if (this.ticketReplyAssignType && !assignPayload) {
        this.handleApiError({ message: 'Destinatario assegnazione non valido' })
        return
      }

      if (!message && !assignPayload && !shouldChangeStatus) {
        return
      }

      if (!message && this.requiresCallTransferArea(this.ticketReplyCallTransferSuccess)) {
        this.handleApiError({ message: 'Per salvare il trasferimento chiamata devi inserire una risposta' })
        return
      }

      if (
        this.requiresCallTransferArea(this.ticketReplyCallTransferSuccess)
        && !this.ticketReplyCallTransferAreaId
      ) {
        this.handleApiError({ message: 'Seleziona l\'area verso cui hai provato a trasferire la chiamata' })
        return
      }

      this.sendingTicketReply = true
      try {
        let messageSent = false
        let statusChanged = false
        let assigned = false
        if (message) {
          const callTransfer = this.buildReplyCallTransferPayload()
          const { data } = await this.incallService.sendMessage(ticketId, message, [], { callTransfer })
          if (!data?.success) {
            this.handleApiError({ message: 'Errore nell\'invio del messaggio' })
            return
          }
          messageSent = true
        }

        if (messageSent || assignPayload || shouldChangeStatus) {
          if (shouldChangeStatus) {
            const statusRes = await this.incallService.changeStatus(ticketId, this.ticketReplyStatusId)
            if (!statusRes?.success) {
              this.handleApiError({ message: messageSent ? 'Risposta inviata ma errore nel cambio stato del ticket' : 'Errore nel cambio stato del ticket' })
            } else {
              statusChanged = true
            }
          }
          if (assignPayload) {
            const assignRes = await this.incallService.assignTicket(ticketId, assignPayload)
            if (!assignRes?.success) {
              this.handleApiError({ message: messageSent ? 'Risposta inviata ma errore nell\'assegnazione del ticket' : 'Errore nell\'assegnazione del ticket' })
            } else {
              assigned = true
            }
          }

          if (messageSent) {
            this.ticketReplyMessage = ''
            this.resetTicketReplyCallTransfer()
          }
          if (assigned) {
            this.resetTicketReplyAssignment()
          }
          await this.refreshSelectedTicketAfterAction(ticketId)
          if (this.$notify && (messageSent || statusChanged || assigned)) {
            this.$notify.success({
              title: 'Successo',
              message: this.getTicketReplySuccessMessage(messageSent, statusChanged, assigned),
            })
          }
        }
      } catch (error) {
        this.handleApiError(error, 'Errore nel salvataggio del ticket')
      } finally {
        this.sendingTicketReply = false
      }
    },

    viewTicket(ticket) {
      this.openTicket(ticket, 'view')
    },

    editTicket(ticket) {
      this.selectedTicket = ticket
    },

    closeModal() {
      this.endTicketModalDrag()
      this.showTicketModal = false
      this.showTicketActionModal = false
      this.showAddTicketModal = false
      this.showTicketNotificationModal = false
      this.showActionModal = false
      this.selectedTicket = {}
      this.ticketForAction = null
      this.ticketModalMode = 'view'
      this.resetTicketReplyForm()
      this.loadingTicketDetails = false
      this.sendingTicketReply = false
      this.loadingTicketNotificationPreview = false
      this.sendingTicketNotificationEmail = false
      this.ticketNotificationPreview = null
      this.creatingTicketData = null
      this.ticketModalDrag = { active: false, startX: 0, startY: 0, deltaX: 0, deltaY: 0 }
      this.resetNewTicketFormState()
    },

    onCallTransferSuccessChange() {
      if (!this.requiresCallTransferArea(this.newTicket.callTransferSuccess)) {
        this.newTicket.callTransferAreaId = null
      }
    },

    requiresCallTransferArea(value) {
      return value === true || value === false
    },

    startTicketModalDrag(e) {
      if (e.target.closest('.close-button')) return
      this.ticketModalDrag = {
        ...this.ticketModalDrag,
        active: true,
        startX: e.clientX,
        startY: e.clientY
      }
      document.addEventListener('mousemove', this.onTicketModalDrag)
      document.addEventListener('mouseup', this.endTicketModalDrag)
    },

    onTicketModalDrag(e) {
      if (!this.ticketModalDrag.active) return
      const dx = e.clientX - this.ticketModalDrag.startX
      const dy = e.clientY - this.ticketModalDrag.startY
      this.ticketModalDrag = {
        ...this.ticketModalDrag,
        deltaX: this.ticketModalDrag.deltaX + dx,
        deltaY: this.ticketModalDrag.deltaY + dy,
        startX: e.clientX,
        startY: e.clientY
      }
    },

    endTicketModalDrag() {
      this.ticketModalDrag = { ...this.ticketModalDrag, active: false }
      document.removeEventListener('mousemove', this.onTicketModalDrag)
      document.removeEventListener('mouseup', this.endTicketModalDrag)
    },

    async createTicketAndReturnResult() {
      try {
        if (!this.newTicket.tipoDestinatario) {
          throw new Error('Seleziona un tipo di destinatario')
        }

        const hasMotivazione = !!this.newTicket.motivazioneId
        if (!hasMotivazione) {
          if (!this.newTicket.titolo?.trim()) {
            throw new Error('Inserisci un titolo per il ticket')
          }
          if (!this.newTicket.descrizione?.trim()) {
            throw new Error('Inserisci una descrizione per il ticket')
          }
        }

        if (!this.newTicket.recipient) {
          throw new Error('Seleziona un destinatario')
        }

        if (
          this.requiresCallTransferArea(this.newTicket.callTransferSuccess)
          && !this.newTicket.callTransferAreaId
        ) {
          throw new Error('Seleziona l\'area verso cui hai provato a trasferire la chiamata')
        }

        if (!this.entityId) {
          throw new Error('Nessun utente selezionato per collegare il ticket')
        }

        this.loading = true

        const nomeContatto = this.getContactDisplayName()

        const tipoContatto = this.getContactTypeForTicket()
        const prefisso = tipoContatto === 'rappresentante' ? 'Rappresentante' : 'Cliente'

        const motivazioneSelezionata = this.newTicket.motivazioneId
          ? this.motivazioniTicket.find(m => m.id === this.newTicket.motivazioneId)
          : null
        const motivazioneTesto = motivazioneSelezionata?.motivation || ''

        const titoloFinale = motivazioneTesto
          ? `[${motivazioneTesto}] ${(this.newTicket.titolo || '').trim()}`.trim() || motivazioneTesto
          : (this.newTicket.titolo || '').trim()

        const descrizioneBase = (this.newTicket.descrizione || '').trim()
        const contenutoConNome = motivazioneTesto
          ? `${prefisso}: ${nomeContatto}\n\nMotivazione: ${motivazioneTesto}\n\n${descrizioneBase}`
          : `${prefisso}: ${nomeContatto}\n\n${descrizioneBase}`

        let ticketData = {
          title: titoloFinale,
          content: contenutoConNome,
          is_related: this.newTicket.is_related,
          recipient: {},
          related_contact: {
            contact_id: this.getContactIdForTicket(),
            contact_type: this.getContactTypeForTicket()
          }
        }

        if (this.newTicket.motivazioneId) {
          ticketData.id_category_motivation = this.newTicket.motivazioneId
        }

        if (this.newTicket.statusCode) {
          ticketData.status_code = this.newTicket.statusCode
        }

        if (this.newTicket.tipoDestinatario === 'area') {
          ticketData.recipient = {
            id: null,
            type: 'area',
            id_ticket_area: this.newTicket.recipient.id_ticket_area
          }
        } else {
          ticketData.recipient = {
            id: this.newTicket.recipient.id,
            type: this.newTicket.tipoDestinatario,
            id_ticket_area: this.newTicket.recipient.id_ticket_area || 0
          }
        }

        if (this.newTicket.is_related && this.selectedEntity) {
          ticketData.type_entity = { id: (this.linkEntityType?.id || this.linkEntityType?.type || this.linkEntityType) }
          ticketData.entity = { id: this.selectedEntity.id }
        }

        if (this.requiresCallTransferArea(this.newTicket.callTransferSuccess)) {
          ticketData.call_transfer = {
            success: this.newTicket.callTransferSuccess === true,
            id_ticket_area: this.newTicket.callTransferAreaId,
          }
        }

        let response
        try {
          response = await this.incallService.createTicket(ticketData)
        } catch (serviceError) {
          response = await this.createTicketDirect(ticketData)
        }

        let res = response && response.data ? response.data : response
        if (res.success && typeof res.success === 'object') {
          res = res.success
        }
        let success = false
        if (
          res === true ||
          res.success === true ||
          res.data?.success === true ||
          res.ticket_id ||
          res.data?.ticket_id
        ) {
          success = true
        }

        if (success) {
          const ticketId =
            res?.ticket_id ||
            res?.data?.ticket_id ||
            response?.ticket_id ||
            response?.data?.ticket_id ||
            null
          await this.loadAllTickets()
          return {
            success: true,
            message: `Ticket creato con successo per ${nomeContatto}!`,
            ticketId: ticketId ? Number(ticketId) : null,
            ticketTitle: titoloFinale,
            recipient: ticketData.recipient,
          }
        } else {
          throw new Error(response?.error || response?.message || 'Errore nella creazione del ticket')
        }
      } catch (error) {
        return { success: false, error: error.message }
      } finally {
        this.loading = false
      }
    },

    async createTicket() {
      try {
        const result = await this.createTicketAndReturnResult()
        if (result.success) {
          this.$notify && this.$notify.success({ title: 'Successo', message: result.message })
          this.showAddTicketModal = false
          this.endTicketModalDrag()
          if (this.shouldOfferTicketNotificationEmail(result.recipient)) {
            await this.openTicketNotificationDialog(result, false)
          } else {
            this.resetNewTicketFormState()
          }
        } else {
          throw new Error(result.error)
        }
      } catch (error) {
        this.handleApiError(error)
      }
    },

    shouldOfferTicketNotificationEmail(recipient) {
      const type = String(recipient?.type || '').toLowerCase()
      return type === 'client' || type === 'cliente' || type === 'rappresentante'
    },

    async openTicketNotificationDialog(createResult, showReclamoAfterClose = false) {
      if (!this.shouldOfferTicketNotificationEmail(createResult?.recipient)) {
        this.resetNewTicketFormState()
        if (showReclamoAfterClose) {
          this.showReclamoDialog = true
        }
        return
      }

      this.creatingTicketData = {
        ticketId: createResult?.ticketId || null,
        ticketTitle: createResult?.ticketTitle || '',
        recipient: createResult?.recipient || null,
        showReclamoAfterClose,
      }
      this.ticketNotificationPreview = null
      this.showTicketNotificationModal = true
      await this.loadTicketNotificationPreview()
    },

    async loadTicketNotificationPreview() {
      if (!this.creatingTicketData?.recipient) {
        return
      }

      this.loadingTicketNotificationPreview = true
      try {
        const recipientEmail = this.resolveRecipientEmail()
        const payload = {
          ticket_id: this.creatingTicketData.ticketId || undefined,
          title: this.creatingTicketData.ticketTitle || undefined,
          recipient: this.creatingTicketData.recipient,
          recipient_id: this.creatingTicketData.recipient?.id || undefined,
          recipient_type: this.creatingTicketData.recipient?.type || undefined,
          to: recipientEmail || undefined,
          to_email: recipientEmail || undefined,
        }

        const result = await this.incallService.previewTicketNotificationEmail(payload)
        this.ticketNotificationPreview = result?.email_preview || null

        if (!result?.success && this.$notify) {
          this.$notify.warn({
            title: 'Attenzione',
            message: result?.message || 'Anteprima email non disponibile',
          })
        }
      } catch (error) {
        this.ticketNotificationPreview = null
        this.handleApiError(error, 'Errore nel caricamento anteprima email')
      } finally {
        this.loadingTicketNotificationPreview = false
      }
    },

    async sendTicketNotification() {
      if (!this.canSendTicketNotification || this.sendingTicketNotificationEmail) return

      this.sendingTicketNotificationEmail = true
      try {
        const recipientEmail = this.resolveRecipientEmail()
        const previewTo = this.ticketNotificationPreview?.to || this.ticketNotificationPreview?.to_email
        const payload = {
          ticket_id: this.creatingTicketData.ticketId,
          recipient_id: this.creatingTicketData.recipient?.id || undefined,
          recipient_type: this.creatingTicketData.recipient?.type || undefined,
          to: previewTo || recipientEmail || undefined,
          to_email: previewTo || recipientEmail || undefined,
        }
        const result = await this.incallService.sendTicketNotificationEmail(payload)

        if (result?.success) {
          if (this.$notify) {
            this.$notify.success({
              title: 'Successo',
              message: result?.message || 'Email di notifica inviata correttamente',
            })
          }
          this.closeTicketNotificationModal()
        } else {
          this.handleApiError(
            { message: result?.message || 'Invio email non riuscito' },
            'Errore invio notifica email ticket'
          )
        }
      } catch (error) {
        this.handleApiError(error, 'Errore invio notifica email ticket')
      } finally {
        this.sendingTicketNotificationEmail = false
      }
    },

    closeTicketNotificationModal() {
      const shouldShowReclamo = !!this.creatingTicketData?.showReclamoAfterClose
      this.showTicketNotificationModal = false
      this.ticketNotificationPreview = null
      this.creatingTicketData = null
      this.resetNewTicketFormState()

      if (shouldShowReclamo) {
        this.showReclamoDialog = true
      }
    },

    resolveRecipientEmail() {
      const recipient = this.creatingTicketData?.recipient || {}
      const contact = this.contact || {}
      return (
        recipient.email ||
        recipient.pec ||
        contact.email ||
        contact.pec ||
        ''
      ).trim()
    },

    resetNewTicketFormState() {
      this.newTicket = {
        tipo: 'Ticket',
        tipoDestinatario: '',
        titolo: '',
        descrizione: '',
        priorita: 'media',
        stato: 'In Attesa',
        statusCode: 'opened',
        recipient: null,
        recipient_id: null,
        area_id: 1,
        is_related: false,
        motivazioneId: null,
        callTransferSuccess: null,
        callTransferAreaId: null,
      }

      this.recipients = []
      this.recipientSearchQuery = ''
      this.showRecipientSuggestions = false
      this.loadingRecipientSuggestions = false
      this.linkEntityType = ''
      this.entityOptions = []
      this.selectedEntity = null
      this.entitySearch = ''
      this.showEntityList = false

      if (this.recipientSearchTimeout) {
        clearTimeout(this.recipientSearchTimeout)
      }
    },

    getContactIdForTicket() {
      const contact = this.contact || {}

      if (contact.client_id) {
        return contact.client_id
      }

      if (contact.rappresentante_id) {
        return contact.rappresentante_id
      }

      if (contact.contatto_id) {
        return contact.contatto_id
      }

      return contact.id || this.entityId
    },

    getContactTypeForTicket() {
      const contact = this.contact || {}

      if (
        this.entityType === 'rappresentante' ||
        contact.entity_type === 'rappresentante' ||
        contact.is_rappresentante ||
        (typeof contact.id === 'string' && contact.id.startsWith('rappresentante_'))
      ) {
        return 'rappresentante'
      }

      if (contact.contatto_id) {
        return 'contatto'
      }

      if (contact.client_id || contact.entity_type === 'client' || this.entityType === 'client') {
        return 'client'
      }

      return this.entityType || 'client'
    },

    getTicketTypeClass(tipo) {
      switch (tipo?.toLowerCase()) {
        case 'dilazione':
          return 'type-dilazione'
        case 'ticket':
          return 'type-ticket'
        case 'reclamo':
          return 'type-reclamo'
        default:
          return 'type-ticket'
      }
    },

    getStatusClass(status) {
      switch (status?.toLowerCase()) {
        case 'attivo':
          return 'status-active'
        case 'in attesa':
        case 'opened':
          return 'status-pending'
        case 'risolto':
        case 'closed':
          return 'status-resolved'
        case 'scaduto':
          return 'status-expired'
        default:
          return 'status-pending'
      }
    },

    isSystemTicket(ticket) {
      const recipient = (ticket.recipient_name || ticket.destinatario || '').trim().toLowerCase()
      const recipientType = (ticket.recipient_type || ticket.type_recipient || '').trim().toLowerCase()
      return recipient === 'log' || recipientType === 'log'
    },

    nextUserTicketsPage() {
      if (this.currentPage < this.totalPagesUserTickets) {
        this.currentPage++
      }
    },

    previousUserTicketsPage() {
      if (this.currentPage > 1) {
        this.currentPage--
      }
    },

    nextSystemTicketsPage() {
      if (this.currentPageSystemTickets < this.totalPagesSystemTickets) {
        this.currentPageSystemTickets++
      }
    },

    previousSystemTicketsPage() {
      if (this.currentPageSystemTickets > 1) {
        this.currentPageSystemTickets--
      }
    },

    handleTicketSearch() {
      if (this.ticketSearchTimeout) {
        clearTimeout(this.ticketSearchTimeout)
      }

      this.ticketSearchTimeout = setTimeout(() => {
        this.currentPage = 1
        this.currentPageSystemTickets = 1
      }, 300)
    },

    clearTicketSearch() {
      this.ticketSearchQuery = ''
      this.currentPage = 1
      this.currentPageSystemTickets = 1
    },

    async showReclamoConfirmation() {
      const result = await this.createTicketAndReturnResult()
      if (result.success) {
        this.$notify && this.$notify.success({ title: 'Successo', message: result.message })
        this.showAddTicketModal = false
        this.endTicketModalDrag()
        if (this.shouldOfferTicketNotificationEmail(result.recipient)) {
          await this.openTicketNotificationDialog(result, true)
        } else {
          this.resetNewTicketFormState()
          this.showReclamoDialog = true
        }
      } else {
        this.handleApiError(new Error(result.error))
      }
    },

    async createTicketWithoutReclamo() {
      this.showReclamoDialog = false
      this.showReclamoLink = false
      await this.loadAllTickets()
    },

    async createTicketWithReclamo() {
      if (!this.showReclamoLink) {
        this.showReclamoLink = true
      } else {
        this.showReclamoDialog = false
        this.showReclamoLink = false
        await this.loadAllTickets()
      }
    },

    async closeReclamoDialog() {
      this.showReclamoDialog = false
      this.showReclamoLink = false
      await this.loadAllTickets()
    },

    getContactDisplayName() {
      const contact = this.contact
      if (!contact && !this.entityId) {
        return 'Cliente/Rappresentante non selezionato'
      }
      if (!contact) {
        return String(this.entityId)
      }

      if (contact.ragsoc) {
        return contact.ragsoc
      } else if (contact.nome && contact.cognome) {
        return `${contact.nome} ${contact.cognome}`
      } else if (contact.nome) {
        return contact.nome
      } else if (contact.cognome) {
        return contact.cognome
      } else if (contact.cliente_nome || contact.cliente_cognome) {
        return `${contact.cliente_nome || ''} ${contact.cliente_cognome || ''}`.trim()
      } else {
        return this.entityType === 'rappresentante' ? 'Rappresentante' : 'Cliente'
      }
    }
  }
}
</script>

<style scoped>
.section-title {
  font-size: 1.3rem;
  font-weight: 600;
  margin: 0;
  color: white;
}

.ticket-search-container {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
  padding: 1rem;
}

.ticket-search-container .search-wrapper {
  position: relative;
  max-width: 400px;
}

.ticket-search-container input {
  width: 100%;
  padding: 8px 12px 8px 35px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.ticket-search-container input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.ticket-search-container .pi-search {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  font-size: 0.9rem;
  pointer-events: none;
}

.ticket-search-container button {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 4px;
  border-radius: 2px;
  transition: color 0.2s ease;
}

.ticket-search-container button:hover {
  color: #333;
  background-color: rgba(0, 0, 0, 0.05);
}

.ticket-search-container .search-info {
  margin-top: 8px;
  font-size: 0.85rem;
  color: #666;
  font-style: italic;
}

.tickets-table-container {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(13, 60, 97, 0.1);
  overflow: hidden;
  border: 1px solid rgba(13, 60, 97, 0.1);
  margin-top: 2rem;
}

.tickets-subsection {
  border-top: 1px solid #e8edf2;
}

.tickets-subsection:first-of-type {
  border-top: none;
}

.tickets-subsection-title {
  margin: 0;
  padding: 0.85rem 1.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: #0d3c61;
  background-color: #f8fafc;
  border-bottom: 1px solid #e8edf2;
}

.tickets-subsection-system .tickets-subsection-title {
  color: #5a6a7a;
}

.ticket-action-preview {
  margin: 0 0 0.5rem;
  font-weight: 600;
  color: #0d3c61;
}

.ticket-action-hint {
  margin: 0 0 1rem;
  color: #666;
  font-size: 0.9rem;
}

.ticket-action-buttons {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.ticket-action-btn {
  flex: 1;
  min-width: 140px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.incall-tickets-block .pi {
  font-family: 'primeicons' !important;
  font-style: normal !important;
  font-weight: normal !important;
  line-height: 1;
  text-transform: none;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.ticket-details-summary {
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e8edf2;
}

.ticket-conversation-section {
  margin-top: 0.5rem;
}

.ticket-history-section {
  margin-bottom: 1rem;
}

.ticket-history-table th,
.ticket-history-table td {
  font-size: 0.85rem;
}

.ticket-conversation-title {
  margin: 0 0 0.75rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: #0d3c61;
}

.ticket-conversation-loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #666;
  padding: 1rem 0;
}

.ticket-messages-list {
  max-height: 320px;
  overflow-y: auto;
  padding: 0.5rem;
  background: #f8fafc;
  border: 1px solid #e8edf2;
  border-radius: 8px;
}

.ticket-message {
  margin-bottom: 0.75rem;
  padding: 0.75rem;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e8edf2;
}

.ticket-message-own {
  background: #eef6ff;
  border-color: #cfe3f8;
}

.ticket-message-meta {
  font-size: 0.75rem;
  color: #666;
  margin-bottom: 0.35rem;
}

.ticket-message-content {
  font-size: 0.9rem;
  color: #333;
  word-break: break-word;
}

.ticket-message-attachments {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-top: 0.6rem;
}

.ticket-message-attachment {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.45rem 0.6rem;
  border-radius: 8px;
  background: #f3f6f9;
  border: 1px solid #e1e8ef;
}

.ticket-message-attachment-name {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
  font-size: 0.82rem;
  color: #1f2937;
  word-break: break-word;
}

.ticket-message-attachment-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  flex-shrink: 0;
}

.ticket-message-attachment-actions button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.8rem;
  height: 1.8rem;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #0d3c61;
  cursor: pointer;
}

.ticket-message-attachment-actions button:hover {
  background: #e4eef6;
}

.ticket-reply-form {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e8edf2;
  width: 100%;
}

.ticket-reply-form .form-textarea {
  width: 100%;
  box-sizing: border-box;
  display: block;
}

.ticket-reply-transfer-fields {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.call-transfer-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 600;
}

.call-transfer-badge--success {
  color: #166534;
  background: #ecfdf3;
}

.call-transfer-badge--failed {
  color: #b91c1c;
  background: #fef2f2;
}

.ticket-message-transfer {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 0.45rem;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
}

.fatture-table-header {
  background: linear-gradient(135deg, #0d3c61, #1a5490);
  color: white;
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 12px 12px 0 0;
}

.table-actions {
  display: flex;
  gap: 0.5rem;
}

.add-ticket-btn {
  background: linear-gradient(135deg, #4CAF50, #45a049);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.add-ticket-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.add-ticket-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.add-icon {
  font-size: 1.2rem !important;
  font-weight: 900 !important;
  color: white !important;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
}

.fatture-table-wrapper {
  overflow-x: auto;
  max-width: 100%;
  max-height: 320px;
  overflow-y: auto;
}

.fatture-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}

.fatture-table thead {
  background: linear-gradient(135deg, #e1edf7, #d1e3f4);
}

.fatture-table th {
  padding: 1rem 0.75rem;
  text-align: left;
  font-weight: 600;
  color: #0d3c61;
  border-bottom: 2px solid #c5d3e4;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.fatture-table td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid #e8eef5;
  color: #374151;
  font-weight: 500;
}

.fatture-table tbody tr:nth-child(even) {
  background-color: #f8fafc;
}

.fatture-table tbody tr:hover {
  background-color: #e8f4fd;
  transition: background-color 0.2s ease;
}

.text-center {
  text-align: center !important;
}

.table-row {
  transition: all 0.2s ease;
}

.table-row:hover {
  transform: translateX(2px);
}

.ticket-type {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.2s ease;
}

.ticket-type:hover {
  transform: scale(1.05);
}

.type-dilazione {
  background: #e8f5e8;
  color: #2d5016;
}

.type-ticket {
  background: #e8f4fd;
  color: #1e40af;
}

.type-reclamo {
  background: #fef2f2;
  color: #dc2626;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.2s ease;
}

.status-badge:hover {
  transform: scale(1.05);
}

.status-active {
  background: #d1fae5;
  color: #065f46;
}

.status-pending {
  background: #fef3c7;
  color: #92400e;
}

.status-resolved {
  background: #dbeafe;
  color: #1e40af;
}

.status-expired {
  background: #fee2e2;
  color: #dc2626;
}

.fattura-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}

.action-btn {
  background: white;
  border: 2px solid;
  padding: 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: bold;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.action-btn:hover .table-icon {
  transform: scale(1.1);
}

.view-btn {
  border-color: #6c757d;
  color: #6c757d;
}

.view-btn:hover {
  background: #6c757d;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
}

.table-icon {
  font-size: 1.1rem !important;
  line-height: 1;
  transition: all 0.2s ease;
}

.no-data-message {
  text-align: center;
  padding: 2rem;
  color: #64748b;
  font-style: italic;
  background: linear-gradient(135deg, #f8fafc, #e2e8f0);
  border-radius: 8px;
  margin: 1rem;
}

.no-data-message::before {
  content: '📋';
  display: block;
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: #f8fafc;
  border-top: 1px solid #e8eef5;
}

.pagination-info {
  font-size: 0.85rem;
  color: #64748b;
  font-weight: 500;
}

.pagination-controls {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.pagination-btn {
  background: white;
  border: 1px solid #d1d5db;
  color: #374151;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  min-width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-btn:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #9ca3af;
  transform: translateY(-1px);
}

.pagination-btn:disabled {
  background: #f9fafb;
  color: #d1d5db;
  cursor: not-allowed;
  border-color: #e5e7eb;
}

.page-indicator {
  padding: 0.5rem 1rem;
  font-weight: 600;
  color: #0d3c61;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  min-width: 80px;
  text-align: center;
}

.pagination-icon {
  font-size: 0.85rem;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 1000px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalAppear 0.3s ease-out;
  max-height: 90vh;
  overflow-y: auto;
  font-family: 'Montserrat', sans-serif !important;
}

.modal-large {
  max-width: 900px;
}

.modal-small {
  max-width: 400px;
  width: 90%;
}

.modal-ticket {
  max-width: 520px;
  width: 95%;
}

.modal-overlay-ticket {
  background-color: rgba(0, 0, 0, 0.35);
  backdrop-filter: none;
}

.modal-drag-handle {
  cursor: grab;
  user-select: none;
}

.modal-drag-handle:active {
  cursor: grabbing;
}

.modal-ticket .modal-body {
  max-height: 50vh;
}

@keyframes modalAppear {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.modal-header h3 {
  font-family: 'Montserrat', sans-serif !important;
  font-weight: 600 !important;
  margin: 0 !important;
}

.modal-header:not(.modal-header-view):not(.modal-header-create) h3 {
  color: rgb(0, 0, 0) !important;
}

.modal-header-view h3,
.modal-header-create h3 {
  color: #fff !important;
}

.modal-header-view {
  background: linear-gradient(135deg, #0d3c61, #1a5490);
}

.modal-header-create {
  background: linear-gradient(135deg, #10b981, #059669);
}

.close-button {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 1.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.close-button:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.modal-body {
  padding: 1.5rem;
  max-height: 60vh;
  overflow-y: auto;
  font-family: 'Montserrat', sans-serif !important;
}

.ticket-details {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e8eef5;
}

.detail-row:last-child {
  border-bottom: none;
}

.modal-footer {
  padding: 1.5rem;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  font-family: 'Montserrat', sans-serif !important;
}

.btn-secondary {
  background: #64748b;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  min-width: 100px;
  font-family: 'Montserrat', sans-serif !important;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-secondary:hover {
  background: #475569;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(100, 116, 139, 0.3);
}

.btn-primary {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  min-width: 120px;
  font-family: 'Montserrat', sans-serif !important;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #059669, #047857);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: none !important;
}

.ticket-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #0d3c61;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: 'Montserrat', sans-serif !important;
}

.form-label i {
  font-size: 1rem;
  color: #3388b3;
}

.form-label .opzionale {
  font-weight: 400;
  font-size: 0.75rem;
  color: #64748b;
  text-transform: none;
}

.input-with-prefix {
  display: flex;
  align-items: center;
  border: 2px solid #e1edf7;
  border-radius: 8px;
  background: white;
  overflow: hidden;
}

.input-with-prefix .input-prefix {
  padding: 0.875rem 0 0.875rem 1rem;
  font-weight: 600;
  color: #3388b3;
  font-size: 0.9rem;
  white-space: nowrap;
  background: #f0f7ff;
  border-right: 1px solid #e1edf7;
}

.input-with-prefix .form-input {
  border: none;
  flex: 1;
}

.input-with-prefix .form-input:focus {
  box-shadow: none;
}

.descrizione-motivazione-prefix {
  padding: 0.5rem 1rem;
  background: #f0f7ff;
  border: 1px solid #e1edf7;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  color: #0d3c61;
  margin-bottom: 0.5rem;
}

.form-input,
.form-select {
  padding: 0.875rem 1rem;
  border: 2px solid #e1edf7;
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: 'Montserrat', sans-serif !important;
  transition: all 0.3s ease;
  background: white;
  font-weight: 500;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.form-textarea {
  padding: 0.875rem 1rem;
  border: 2px solid #e1edf7;
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: 'Montserrat', sans-serif !important;
  transition: all 0.3s ease;
  background: white;
  font-weight: 500;
  resize: vertical;
  min-height: 120px;
}

.form-textarea:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.form-select {
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1rem;
  padding-right: 3rem;
  cursor: pointer;
}

.char-counter {
  align-self: flex-end;
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 500;
}

.checkbox-group {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.checkbox-label {
  font-weight: 500;
  color: #0d3c61;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif !important;
}

.selected-contact-info {
  background: linear-gradient(135deg, #f0f7ff, #e8f4fd);
  border: 1px solid #c5d3e4;
  border-radius: 8px;
  padding: 1rem;
}

.info-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  color: #0d3c61;
  font-weight: 600;
}

.contact-summary {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.contact-details {
  font-size: 0.9rem;
  color: #64748b;
}

.entity-dropdown {
  margin-top: 0.5rem;
  background: #fff;
  border: 2px solid #e1edf7;
  border-radius: 8px;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 8px 20px rgba(13, 60, 97, 0.15);
}

.entity-item {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid #e8eef5;
}

.entity-item:hover {
  background: #e8f4fd;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-spinner {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.loading-spinner i {
  font-size: 2rem;
  color: #3388b3;
}

.loading-spinner span {
  font-weight: 600;
  color: #0d3c61;
  font-family: 'Montserrat', sans-serif !important;
}

.message-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1rem;
  padding: 1rem 0;
}

.message-content i {
  font-size: 3rem;
}

.message-content i.pi-question-circle {
  color: #3388b3;
}

.message-content p {
  margin: 0;
  font-size: 1.1rem;
  line-height: 1.5;
}

@media (max-width: 768px) {
  .ticket-form {
    gap: 1rem;
  }

  .modal-large {
    max-width: 95%;
  }

  .pagination-container {
    flex-direction: column;
    gap: 0.75rem;
  }

  .action-btn {
    width: 32px;
    height: 32px;
  }

  .ticket-type,
  .status-badge {
    font-size: 0.7rem;
  }
}

.recipient-search-container {
  position: relative;
}

.loading-suggestions {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
  pointer-events: none;
}

.loading-suggestions i {
  font-size: 0.85rem;
}

.suggestions-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1100;
  background: white;
  border: 1px solid #cbd5e1;
  border-top: none;
  border-radius: 0 0 6px 6px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12);
  max-height: 220px;
  overflow-y: auto;
}

.suggestion-item {
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 0.15s ease;
}

.suggestion-item:hover {
  background-color: #f8fafc;
}

.suggestion-item:last-child {
  border-bottom: none;
}

.suggestion-primary {
  font-weight: 500;
  color: #1e293b;
  font-size: 0.9rem;
}

.suggestion-secondary {
  font-size: 0.78rem;
  color: #64748b;
  margin-top: 2px;
}

.suggestion-empty {
  padding: 12px;
  color: #94a3b8;
  font-style: italic;
  text-align: center;
  font-size: 0.85rem;
}

.selected-recipient-badge {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.5rem;
  padding: 0.45rem 0.65rem;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
  background: #f0fdf4;
  color: #166534;
  font-size: 0.85rem;
}

.clear-recipient-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #166534;
  cursor: pointer;
}

.clear-recipient-btn:hover {
  background: rgba(22, 101, 52, 0.1);
}

.add-ticket-btn:focus,
.action-btn:focus,
.pagination-btn:focus,
.btn-secondary:focus,
.btn-primary:focus {
  outline: 2px solid #3388b3;
  outline-offset: 2px;
}

.ticket-email-preview-loading {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  color: #64748b;
}

.ticket-email-preview-empty {
  color: #64748b;
}

.ticket-email-preview-body {
  margin-top: 0.8rem;
}

.ticket-email-preview-body pre {
  margin: 0.4rem 0 0;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #f8fafc;
  color: #334155;
  font-size: 0.85rem;
  white-space: pre-wrap;
  max-height: 220px;
  overflow-y: auto;
}

.status-ok {
  color: #166534;
  font-weight: 600;
}

.status-ko {
  color: #b91c1c;
  font-weight: 600;
}
</style>
